// src/session/SessionContext.jsx
import React, {
    createContext, useContext, useState, useEffect, useMemo, useCallback, useRef
} from "react";
import { ACCOUNT_CHOICE_ENABLED } from '../config/features.js';
import { DECISION } from '../chargerUx/canonical';
import { applySnapshot } from '../chargerUx/canonicalStore';
import { getCustomerSession, stopSession as apiStopSession, clearSessionPersistence, createCustomerSession } from '../api/sessionsApi.js';
import { mintSessionStreamTicket, isTerminalMintStatus } from '../api/streamTicketsApi.js';
import { getAccessToken } from '../auth/tokenStorage.js';
import { installAuthFailedInterceptor } from '../api/authFailedInterceptor.js';
import { cancelAndCleanup, cancelIfStillPreCharge } from '../utils/cancelSession.js';
import { isOwnedByAnotherTab } from '../utils/sessionTabOwnership.js';
import { STEPS, PAYMENT_STEPS } from '../constants/steps.js';
import { isPreauthActive } from '../utils/preauthRegistry.js';
import { saveActiveSession, clearActiveSession } from '../utils/sessionPersistence.js';
import {
  LIFECYCLE_STATES,
  normalizeOcppStatus,
  deriveLifecycleState,
  hasActiveMeterActivity,
  isBackendTerminalStatus,
} from '../utils/sessionLifecycle.js';

const SessionContext = createContext(null);

// Storage & expiry
const STORAGE_KEY = "ev.session.v1";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes
// Only while the driver is away at /auth. Long enough for a slow email plus
// onboarding; short enough that an abandoned signup does not resurrect a
// charger selection much later.
const AUTH_ROUND_TRIP_TTL_MS = 60 * 60 * 1000; // 60 minutes

const defaultState = {
    step: 1,
    paymentStep: PAYMENT_STEPS.DETAILS,  // Payment substep: 'details' | 'receipt' | 'processing'
    status: "idle",
    amountCents: 0,
    currency: "BBD",
    stationId: null,
    publicId: null,          // Public charger ID (GCEV format, e.g., GCEV0920879)
    sessionId: null,         // DEPRECATED: legacy field, use sessionUid instead
    sessionUid: null,        // Server-side session UID (primary session identifier)
    // Physical OCPP connector id the customer has selected on the
    // charger detail screen. Explicitly `null` at first — the
    // EvProfile picker either auto-selects the sole connector on a
    // single-connector charger or requires the driver to tap one on
    // a multi-connector charger before enabling Get Started. Session
    // creation refuses to run with a null / non-integer connectorId,
    // so a stray `state.connectorId` from a bug can no longer
    // silently pin every customer to connector 1.
    connectorId: null,
    stationData: null,       // raw payload from API (e.g., device)
    stationStatus: null,     // e.g., device?.station_status?.[0] or normalized status object
    stationMeta: null,       // { name, location, connector, maxPower, photo } (from evDataMap/static)
    pricingSnapshot: null,   // Locked-in pricing when session created: { rate_per_kwh, session_fee, currency, min_charge_amount } — the server tariff when the API applied one
    authorization: null,     // P4b: the session-start authorization block returned on create / attach (mode, decision, billingMode, fallbackFrom, …)
    // Platform cleanup, Phase 3: the backend's charging access outcome
    // (greencharge.access-outcome.v1) for this charger and the vehicle the
    // driver proved, held only while the FLEET_ACCESS_RESULT step shows it.
    // Printed as received -- the app never derives one.
    accessOutcome: null,
    // True when the attempt that already exists may have been created with,
    // or had attached, the vehicle the outcome is about. The result step then
    // releases that attempt before the card flow or a different vehicle, so
    // the vehicle never stays on a public session.
    accessOutcomeSessionHasVehicle: false,
    authorizedAmount: null,  // Locked-in authorized amount in cents (when payment authorized)
    sessionBudgetCents: null, // Session budget in cents (authorizedAmount if payments enabled, else min_charge_amount)
    live: {
        status: null,
        meter_value: 0,
        meter_start: null,    // Meter value when charging started (for delta calculation, kWh)
        meter_stop: null,     // Meter value when charging stopped (for final delta fallback, kWh)
        cumulativeMeter: null, // Latest raw cumulative meter reading (kWh)
        energyKWh: 0,
        powerKW: 0,
        tx_id: null,
        ts: null,
        startTime: null,      // When charging started
        endTime: null,        // When charging stopped
        // Canonical, normalised view of "what state is this session in?"
        // — see utils/sessionLifecycle.js. We derive this from OCPP status,
        // backend session.status, tx_id, startTime and MeterValues activity
        // so the UI doesn't have to chase the moving target of `status`.
        lifecycleState: LIFECYCLE_STATES.IDLE,
        // Hard "I have seen energy flow with my own eyes" flag. Set on the
        // first MeterValues event and never cleared until the session ends.
        // Used as the override that beats every other heuristic — once true,
        // polling cannot downgrade us back to a pre-charge state.
        hasReceivedMeterValues: false,
        lastMeterValueAt: null,
        // Idle detection — populated by backend SESSION_IDLE_CHANGED events
        // (see services/sessions/sessionIdleDetector.js). `lastReadingAt`
        // is stamped locally whenever a MeterValues frame is applied so
        // the UI can independently detect a stalled meter stream even if
        // the backend hasn't flipped idle_since yet.
        isIdle: false,
        idleSince: null,
        lastReadingAt: null,
        // Stamped each time a MeterValues frame raises energyKWh or
        // cumulativeMeter (or shows non-trivial power). The "Not drawing
        // power" idle banner consults this to suppress itself while the
        // session is genuinely progressing — backend SESSION_IDLE_CHANGED
        // is occasionally late to clear after the vehicle resumes draw.
        lastEnergyIncreaseAt: null,
        // Set by the backend's SESSION_BATTERY_LIKELY_FULL SSE event when
        // the session has been idle long enough AND has delivered enough
        // energy that "battery is probably full" is a confident statement
        // (see sessionIdleDetector.evaluateBatteryLikelyFull on the API).
        // Cleared when the car wakes up (MV progresses) so the banner
        // doesn't linger past resumed draw.
        batteryFullSince: null,
        // Backend-derived string composed from (status, idle_since,
        // battery_full_since) — see services/sessions/chargingActivity.js
        // on sw-ev-api. Populated on cold load from the DTO (so refresh
        // during CHARGE_COMPLETE renders correctly) and refreshed via
        // polling. SSE still updates idleSince / batteryFullSince
        // directly; this field is the compound view.
        //   'CHARGING' | 'PAUSED_BY_VEHICLE' | 'CHARGE_COMPLETE' | null
        chargingActivity: null,
    },
    payment: {
        status: null,         // null | 'authorizing' | 'authorized' | 'capturing' | 'paid' | 'voiding' | 'voided' | 'error'
        orderId: null,        // EV-SESSION-{sessionId}
        transactionId: null,  // Internal transaction ID from backend
        authCode: null,       // Authorization code from provider
        authorizationId: null,// Authorization ID from payment provider (required to start charging)
        error: null,          // Error object { code, message, correlationId }
        correlationId: null,  // For support tracking
        capturedAmount: null, // Final captured amount
        cardLast4: null,      // Last 4 digits of card
        paymentMode: null,    // 'real' | 'demo' - track if payment is demo mode
    },
    // "account" | "guest" | null — the AccountChoice step's answer. Read by
    // App's step routing (guests skip the plate screen) and by ChargeSetup's
    // returning-vehicle prompt, which stays quiet once a driver has declined.
    accountChoice: null,
    // Set when the driver leaves for /auth from AccountChoice. Extends the
    // pre-charging TTL below for that round trip only — see load().
    authRoundTripStartedAt: null,
    receiptInfo: null,       // { fullName, email } from receipt step
    receiptEmailSent: false, // Track if receipt email was sent
    // Redeem/credit code applied on the Pre-Auth Hold screen.
    // Shape: { code, amount, currency } | null.
    // This is a UI-only preview — the code is NOT redeemed on the
    // server until payment bootstrap succeeds. Persisted here so it
    // survives navigation back and forward between the pre-auth,
    // confirm-and-pay, and payment steps.
    appliedCredit: null,
    debugMode: false,        // debug mode disables auto-navigation
    // Vehicle verification fields (Phase 2)
    // IMPORTANT: vehicle is the single source of truth for billing mode and payment requirements
    vehicle: {
        licensePlateRaw: null,          // User-entered plate
        licensePlateNormalized: null,   // Server-normalized plate
        vehicleId: null,                // Server vehicle ID (if verified)
        verified: false,                // Verification status
        billingMode: null,              // 'PUBLIC_CARD' | 'DISCOUNTED_CARD' | 'FLEET_INVOICE' | 'FREE' (null = not verified)
        rateOverride: null,             // Retired: the API no longer returns a per-vehicle rate (server tariff is authoritative)
        paymentRequired: true,          // Derived: true for PUBLIC_CARD/DISCOUNTED_CARD, false for FLEET_INVOICE/FREE
        rateApplied: null,              // Derived: session/station rate (0 for FREE)
        ownerName: null,                // Vehicle owner name from registry (for receipt fallback)
        ownerEmail: null,               // Vehicle owner email from registry (for receipt fallback)
    },
    // Legacy vehicle fields (for backwards compatibility - prefer vehicle.* above)
    vehicleVerified: false,
    verifiedPlate: null,
    vehicleId: null,
    billingMode: null,
    rateOverride: null,
    // Signed-in "Choose a vehicle" selection on Confirm & Pay. Non-null
    // `selectedVehicleId` means the user picked one of their
    // /api/vehicles/mine cars. `selectedVehicle` holds the small display
    // metadata (name + plate) the PIN sub-step needs for its subtitle
    // — never billing rules, never PIN, never anything that should not
    // survive across a browser refresh.
    selectedVehicleId: null,
    selectedVehicle: null,
    // Guest-session ownership proof was refused by the backend guard.
    // Set exclusively by the shared axios interceptor. While true, the
    // whole charging shell renders <AuthRequiredView /> — that unmounts
    // every polling loop for free via the normal effect cleanup. NOT
    // persisted to localStorage, so a page reload starts clean.
    authFailed: false,
    authFailedReason: null,
    updatedAt: Date.now(),
};
/**
 * Session states that are allowed to receive MeterValues updates
 * IMPORTANT: This prevents old sessions from continuing to accumulate energy
 */
const ACTIVE_CHARGING_STATUSES = ['Charging', 'Preparing', 'SuspendedEV', 'SuspendedEVSE'];

/**
 * Terminal session states that indicate charging has ended
 */
const TERMINAL_STATUSES = ['Available', 'Finished', 'Completing'];

/**
 * Check if a session should accept MeterValues
 * @param {object} liveState - Current live session state
 * @param {object} backendSession - Backend session object (optional, for recovery)
 * @returns {boolean} - True if session can receive MeterValues
 */
/**
 * A transaction id that came from the charger (via the server or an SSE
 * event), or null. Recovery used to invent `preparing-<uid>` /
 * `recovering-<uid>` placeholders, and older bundles persisted them in
 * ev.session.v1; neither is evidence that a transaction exists.
 */
function realTxId(value) {
    if (value === null || value === undefined || value === '') return null;
    const s = String(value);
    if (s.startsWith('preparing-') || s.startsWith('recovering-')) return null;
    return value;
}

/**
 * Recovery judged a pre-charge attempt stale and is about to forget it
 * locally. Release it on the server too, or it keeps holding the connector
 * (and any card authorization) until the server's own timeout, and can refuse
 * this driver's next attempt from another browser with CONNECTOR_BUSY.
 *
 * `cancelIfStillPreCharge` asks the server first and only cancels a session
 * that is still pending/authorized with no start and no transaction, so a
 * session that has moved on is never touched. Skipped when another open tab
 * is still driving this session. Fire-and-forget: never delays or fails
 * recovery.
 */
function releaseStaleAttempt(sessionUid) {
    if (!sessionUid) return;
    Promise.resolve()
        .then(() => isOwnedByAnotherTab(sessionUid))
        .then((ownedElsewhere) => {
            if (ownedElsewhere) {
                console.log('[SessionContext] Stale attempt is open in another tab - not releasing', { sessionUid });
                return null;
            }
            return cancelIfStillPreCharge(sessionUid);
        })
        .then((r) => { if (r) console.log('[SessionContext] Stale attempt release', { sessionUid, ...r }); })
        .catch(() => {});
}

function canAcceptMeterValues(liveState, backendSession = null) {
    // RULE 1: If session has ended (has endTime), reject all MeterValues
    if (liveState.endTime) {
        return false;
    }
    
    // RULE 2: If session is in terminal status, reject MeterValues
    if (liveState.status && TERMINAL_STATUSES.includes(liveState.status)) {
        return false;
    }
    
    // RULE 3: Check backend authoritative status for recovery scenarios
    // If backend says session is charging/starting, ALLOW MeterValues even without local tx_id
    // This enables recovery from state mismatch (backend charging, frontend missing tx_id)
    if (backendSession) {
        const backendStatus = backendSession.status;
        if (backendStatus === 'charging' || backendStatus === 'starting') {
            console.log('[SessionContext] ✅ MeterValues ALLOWED via backend authoritative status:', backendStatus);
            return true;
        }
    }
    
    // RULE 4: Local state check - has transaction ID OR start time
    if (liveState.tx_id || liveState.startTime) {
        return true;
    }
    
    // RULE 5: Status-based check - only accept if status is an active charging status
    if (liveState.status && ACTIVE_CHARGING_STATUSES.includes(liveState.status)) {
        return true;
    }
    
    return false;
}

function applyEvent(prev = {}, evt, backendSession = null) {
    if (!evt || !evt.type) return prev;

    switch (evt.type) {
        case "METER_VALUES":
        case "MeterValues": {
        // ============================================================
        // Parse cumulative meter & power from the OCPP frame first so the
        // result is available both for the live path and the late-update
        // (post-StopTransaction) path below.
        //
        // SHAPE TOLERANCE: The OCPP 1.6 raw payload uses camelCase
        // (sampledValue, meterValue). The backend SSE forwards data.meter_value
        // as-is, and some upstream paths normalize to snake_case
        // (sampled_value). Walk both shapes so the local fallback path is not
        // silently no-op'd against camelCase payloads.
        // ============================================================
        let cumulativeMeter = 0; // Raw cumulative meter from charger (kWh)
        let powerKW = prev.powerKW ?? 0;
        let hasEnergyMeasurand = false;

        const readSample = (sample) => {
            const measurand = sample?.measurand || 'Energy.Active.Import.Register';
            const rawValue = parseFloat(sample?.value);
            if (!Number.isFinite(rawValue)) return;
            const unit = sample?.unit;
            if (measurand === 'Energy.Active.Import.Register') {
                hasEnergyMeasurand = true;
                // OCPP 1.6: unit may be 'Wh' or 'kWh'. Default to Wh when absent
                // (matches charger behaviour in the wild).
                cumulativeMeter = unit === 'kWh' ? rawValue : rawValue / 1000;
            } else if (measurand === 'Power.Active.Import') {
                powerKW = unit === 'kW' ? rawValue : rawValue / 1000;
            }
        };

        if (Array.isArray(evt.meter_value)) {
            evt.meter_value.forEach(reading => {
                const samples = Array.isArray(reading?.sampled_value)
                    ? reading.sampled_value
                    : Array.isArray(reading?.sampledValue)
                        ? reading.sampledValue
                        : null;
                if (samples) samples.forEach(readSample);
            });
        }

        // ============================================================
        // Resolve session energy delta.
        //
        // PREFERRED: Trust evt.energyKWh from the backend. The publisher
        // (webhooks.js / utils/helper.js) computes the delta against the
        // authoritative session.meter_start_kwh persisted at StartTransaction
        // ingestion. Using it avoids the failure mode where StartTransaction
        // SSE was missed (SSE connected late, reconnect, etc.) and the local
        // meter_start is null — which previously caused the first MeterValue
        // to become its own baseline and produce a 0 kWh delta for several
        // minutes, falsely triggering the "No energy detected" banner.
        //
        // FALLBACK: When the backend did not ship a usable energyKWh, recompute
        // locally as (cumulativeMeter - meter_start), initializing meter_start
        // from the first MeterValue if still unknown.
        // ============================================================
        // Trust backend ONLY when it provides a POSITIVE value. A backend
        // energyKWh=0 means "I cannot compute the delta" (usually because
        // session.meter_start_kwh wasn't persisted at StartTransaction
        // ingestion). Previously we accepted >=0 and treated 0 as
        // authoritative — that suppressed the local meter_start seed at
        // the first-MeterValues branch below, leaving sessionDelta stuck
        // at 0 even while cumulative meter climbed. Treating 0 as null
        // routes us to the local fallback (seed meter_start from the
        // first cumulative reading, then sessionDelta = cumulative - seed).
        // Final settlement remains backend-authoritative via the SUMMARY
        // path (getFinalSessionEnergy) — this only governs the live UI.
        const backendDeltaKWh =
            typeof evt.energyKWh === 'number' && Number.isFinite(evt.energyKWh) && evt.energyKWh > 0
                ? evt.energyKWh
                : null;

        // ════════════════════════════════════════════════════════════════
        // LATE METERVALUES PATH (after StopTransaction / endTime is set).
        //
        // The previous logic dropped every event past endTime. Real chargers
        // routinely send a final, higher MeterValues frame after
        // StopTransaction — that frame carries the final session energy. If
        // we drop it, the receipt is locked to whatever lower value happened
        // to be in state at the moment of StopTransaction (often 0).
        //
        // Accept late events under strict monotonic rules: only ever raise
        // energyKWh / cumulativeMeter (never lower them, never restart the
        // session, never clear endTime, never re-enable the stop button).
        // ════════════════════════════════════════════════════════════════
        if (prev.endTime) {
            const prevEnergy = Number(prev.energyKWh) || 0;
            const prevCumulative = Number(prev.cumulativeMeter) || 0;
            const candidateEnergy = backendDeltaKWh != null
                ? backendDeltaKWh
                : (hasEnergyMeasurand && prev.meter_start != null
                    ? Math.max(0, cumulativeMeter - Number(prev.meter_start))
                    : prevEnergy);
            const nextEnergy = Math.max(prevEnergy, candidateEnergy);
            const nextCumulative = hasEnergyMeasurand
                ? Math.max(prevCumulative, cumulativeMeter)
                : prevCumulative;

            if (nextEnergy > prevEnergy || nextCumulative > prevCumulative) {
                console.log('[SessionContext] ⏱️ Late MeterValues (post-stop) raised final energy', {
                    prevEnergy: prevEnergy.toFixed(3),
                    nextEnergy: nextEnergy.toFixed(3),
                    prevCumulative: prevCumulative.toFixed(3),
                    nextCumulative: nextCumulative.toFixed(3),
                    backendEnergyKWh: backendDeltaKWh != null ? backendDeltaKWh.toFixed(3) : null,
                });
                return {
                    ...prev,
                    energyKWh: nextEnergy,
                    cumulativeMeter: nextCumulative,
                    // meter_stop reflects the latest observed cumulative reading
                    // so getFinalSessionEnergy's (meter_stop - meter_start)
                    // fallback can recover the true delivered energy.
                    meter_stop: nextCumulative > 0 ? nextCumulative : (prev.meter_stop ?? null),
                    // Stamp ts to keep ordering correct, but do NOT touch
                    // status / lifecycleState / endTime / tx_id.
                    ts: evt.ts ?? prev.ts ?? null,
                };
            }

            // Non-monotonic / no new info — drop silently.
            return prev;
        }

        // ============================================================
        // LIVE PATH (session still active).
        // ============================================================
        if (!canAcceptMeterValues(prev, backendSession)) {
            console.log('[SessionContext] 🚫 MeterValues IGNORED - session not eligible', {
                status: prev.status,
                hasEndTime: !!prev.endTime,
                hasTxId: !!prev.tx_id,
                hasStartTime: !!prev.startTime,
                backendStatus: backendSession?.status,
                reason: prev.endTime ? 'session_ended' :
                        prev.status && TERMINAL_STATUSES.includes(prev.status) ? 'terminal_status' :
                        !prev.tx_id && !prev.startTime ? 'no_active_transaction' :
                        'invalid_status'
            });
            return prev;
        }

        let meterStart = prev.meter_start;
        let updatedState = { ...prev };

        // Only initialize meter_start locally when the backend didn't give us
        // a delta to trust AND we actually have a cumulative reading to use.
        if (backendDeltaKWh == null && hasEnergyMeasurand && (meterStart === null || meterStart === undefined)) {
            console.log('[SessionContext] 📍 Initializing meter_start from first MeterValues (fallback)', {
                cumulativeMeter: cumulativeMeter.toFixed(3),
                tx_id: prev.tx_id,
                status: prev.status
            });
            meterStart = cumulativeMeter;
            updatedState.meter_start = cumulativeMeter;
        }

        const computedEnergyKWh = backendDeltaKWh != null
            ? backendDeltaKWh
            : (hasEnergyMeasurand && meterStart != null
                ? Math.max(0, cumulativeMeter - meterStart)
                : (Number(prev.energyKWh) || 0));

        // Monotonic guard for the live UI: session energy must not shrink
        // mid-session. Protects against a backend frame whose meter_start
        // is briefly out of sync (e.g. backfill timing) shipping a smaller
        // delta than what we've already observed. Mirrors the late-MV
        // path above (which also clamps with Math.max). Final settlement
        // is unaffected — getFinalSessionEnergy still prefers authoritative
        // backend values when present.
        const sessionEnergyKWh = Math.max(Number(prev.energyKWh) || 0, computedEnergyKWh);

        const energySource = backendDeltaKWh != null
            ? 'backend'
            : (hasEnergyMeasurand ? 'fallback_local_delta' : 'no_energy_measurand_kept_prior');

        const meterStartLog = meterStart != null ? Number(meterStart).toFixed(3) : 'null';

        console.log('[SessionContext] ✅ MeterValues APPLIED', {
            status: prev.status,
            energySource,
            backendEnergyKWh: backendDeltaKWh != null ? backendDeltaKWh.toFixed(3) : null,
            cumulativeMeter: cumulativeMeter.toFixed(3),
            meterStart: meterStartLog,
            sessionDelta: sessionEnergyKWh.toFixed(3),
            powerKW: powerKW.toFixed(2),
            tx_id: prev.tx_id,
            hasEnergyMeasurand,
            meterStartSource: prev.meter_start !== null && prev.meter_start !== undefined ? 'StartTransaction' : 'FirstMeterValues'
        });

        // ═══════════════════════════════════════════════════════════════
        // METERVALUES OVERRIDE RULE
        // First-hand evidence beats every other status signal. Once
        // MeterValues arrive we know the charger IS charging, so we:
        //   * force status='Charging' (PascalCase, matches every UI check)
        //   * force lifecycleState='charging'
        //   * set hasReceivedMeterValues=true (sticky)
        //   * stamp lastMeterValueAt for timeout/idle math
        // Subsequent polling cannot downgrade these because the polling
        // path consults hasActiveMeterActivity() before overwriting.
        // ═══════════════════════════════════════════════════════════════
        const nowIso = new Date().toISOString();
        const stampedAt = evt.ts ?? nowIso;

        // cumulativeMeter is only valid this frame if the Energy measurand
        // was actually present; otherwise keep the previously-known value
        // (power-only frames must not reset cumulativeMeter to 0).
        const nextCumulativeMeter = hasEnergyMeasurand
            ? cumulativeMeter
            : (Number(prev.cumulativeMeter) || 0);

        // Detect "session is genuinely progressing" so we can self-correct a
        // stale backend `isIdle: true` flag. Any of these is sufficient:
        //   - session delta moved up (post-monotonic clamp)
        //   - raw cumulative meter moved up this frame
        //   - we observe non-trivial instantaneous power (>0.1 kW)
        // When that holds, clear the local idle flag — the next backend
        // SESSION_IDLE_CHANGED(isIdle=false) can be minutes late, and we
        // shouldn't lie to the UI in the meantime. Also stamp
        // lastEnergyIncreaseAt so the idle banner's grace-period check
        // can suppress itself even if isIdle hasn't flipped yet.
        const prevEnergy = Number(prev.energyKWh) || 0;
        const prevCumulative = Number(prev.cumulativeMeter) || 0;
        const energyIncreased = sessionEnergyKWh > prevEnergy;
        const cumulativeIncreased = hasEnergyMeasurand && nextCumulativeMeter > prevCumulative;
        const drawingPower = Number.isFinite(powerKW) && powerKW > 0.1;
        const sessionProgressing = energyIncreased || cumulativeIncreased || drawingPower;

        return {
            ...updatedState,
            status: 'Charging',
            lifecycleState: LIFECYCLE_STATES.CHARGING,
            hasReceivedMeterValues: true,
            lastMeterValueAt: stampedAt,
            cumulativeMeter: nextCumulativeMeter,
            meter_value: evt.meter_value, // Keep original for debugging
            energyKWh: sessionEnergyKWh,  // Session delta, not cumulative meter
            powerKW: powerKW,
            ts: evt.ts ?? prev.ts ?? null,
            // Stamp the client-side "last reading seen" clock. Used by the
            // ChargeMonitoring idle banner to detect stalled streams even
            // before the backend's SESSION_IDLE_CHANGED fires.
            lastReadingAt: stampedAt,
            // Stamp the most recent moment we observed real progress (energy
            // rising or power being drawn). Read by the idle-banner gate
            // to suppress "Not drawing power" while charging is actually
            // happening — even if the backend's idle flag is stale.
            lastEnergyIncreaseAt: sessionProgressing ? stampedAt : (prev.lastEnergyIncreaseAt ?? null),
            // Self-correct a stale backend idle flag the moment we see
            // progress. Backend SESSION_IDLE_CHANGED is authoritative when
            // it fires, but it can lag by minutes; first-hand evidence wins.
            isIdle: sessionProgressing ? false : prev.isIdle,
            idleSince: sessionProgressing ? null : prev.idleSince,
            // Same policy for batteryFullSince — backend will eventually
            // publish SESSION_BATTERY_LIKELY_FULL with since:null when its
            // idle detector clears, but we don't want to wait for it when
            // we have first-hand evidence of resumed draw.
            batteryFullSince: sessionProgressing ? null : prev.batteryFullSince,
            // CRITICAL: Ensure startTime exists when MeterValues arrive.
            // If START_TRANSACTION hasn't arrived yet, the UI gate that shows
            // live data relies on progress.elapsedDisplay which needs startTime.
            // Without this, UI stays stuck on "Waiting for live data..." even
            // though energy is flowing.
            startTime: updatedState.startTime || stampedAt,
        };
        }

        case "SESSION_IDLE_CHANGED":
        // Backend-authoritative idle transition from sessionIdleDetector.
        // Fires only on transitions, so we don't need to guard against churn.
        console.log('[SessionContext] 💤 SESSION_IDLE_CHANGED', {
            isIdle: evt.isIdle,
            idleSince: evt.idleSince
        });
        return {
            ...prev,
            isIdle: !!evt.isIdle,
            idleSince: evt.idleSince || null,
            ts: evt.ts ?? prev.ts ?? null,
        };

        case "SESSION_BATTERY_LIKELY_FULL":
        // Backend-authoritative transition into / out of "battery likely
        // full". `since` is the timestamp the state was entered, or null
        // when the car has woken up. The frontend MV reducer also clears
        // this locally on observed progress (see live MV path) so the
        // banner doesn't linger past resumed draw.
        console.log('[SessionContext] 🔋 SESSION_BATTERY_LIKELY_FULL', {
            since: evt.since,
            session_uid: evt.session_uid,
        });
        return {
            ...prev,
            batteryFullSince: evt.since || null,
            ts: evt.ts ?? prev.ts ?? null,
        };

        case "STATUS_NOTIFICATION":
        case "StatusNotification": {
        // Normalise to PascalCase. Backend / OCPP can occasionally ship
        // lowercase or snake_case which would silently fail downstream
        // `status === 'Preparing'` checks.
        const newStatus = normalizeOcppStatus(evt.status) ?? prev.status ?? null;
        console.log('[SessionContext] 📊 StatusNotification', {
            oldStatus: prev.status,
            newStatus,
            rawStatus: evt.status,
            ts: evt.ts,
        });

        // Once MeterValues are flowing the charger has already decided it's
        // charging — don't let a stray Preparing/Available StatusNotification
        // pull the UI backwards.
        if (hasActiveMeterActivity(prev) && newStatus !== 'Available' && newStatus !== 'Finishing') {
            const keptStatus = (newStatus === 'Charging' || newStatus === 'SuspendedEV' || newStatus === 'SuspendedEVSE')
                ? newStatus
                : 'Charging';
            return {
                ...prev,
                status: keptStatus,
                lifecycleState: LIFECYCLE_STATES.CHARGING,
                ts: evt.ts ?? prev.ts ?? null,
            };
        }

        const nextLifecycle = deriveLifecycleState({
            live: { ...prev, status: newStatus },
        });

        return {
            ...prev,
            status: newStatus,
            lifecycleState: nextLifecycle,
            ts: evt.ts ?? prev.ts ?? null,
        };
        }

        case "START_TRANSACTION":
        case "StartTransaction":
        // ============================================================
        // CRITICAL: Capture meter_start for session delta calculation
        // BUG FIX: Do NOT default to 0 if meter_start is missing!
        // If charger doesn't send meter_start, we'll initialize from first MeterValues
        //
        // UNIT FIX: OCPP 1.6 StartTransaction.meterStart is ALWAYS in Wh.
        // MeterValues handler normalizes cumulative meter to kWh, so meter_start
        // must also be stored in kWh to avoid unit mismatch in delta calculation.
        // Without this, delta = (kWh - Wh) is negative and gets clamped to 0.
        // ============================================================
        const meterStartRaw = evt.meter_start; // Don't use ?? 0 fallback!
        const meterStartKwh = (meterStartRaw !== null && meterStartRaw !== undefined)
            ? meterStartRaw / 1000  // Wh → kWh (OCPP 1.6 always sends Wh)
            : null;
        
        console.log('[SessionContext] 🔌 StartTransaction', {
            tx_id: evt.tx_id,
            meter_start_raw_wh: meterStartRaw !== null && meterStartRaw !== undefined ? meterStartRaw : 'NOT_SET',
            meter_start_kwh: meterStartKwh !== null ? meterStartKwh.toFixed(3) : 'NOT_SET',
            ts: evt.ts
        });
        
        return {
            ...prev,
            tx_id: evt.tx_id ?? prev.tx_id ?? null,
            // Preserve a previously-seeded meter_start (from the first
            // MeterValues fallback path) when StartTransaction arrives
            // without one. Some upstream forwarders strip meter_start
            // from late StartTransaction frames; overwriting with null
            // here would re-break the delta calc we already healed.
            meter_start: meterStartKwh ?? prev.meter_start ?? null,
            status: "Charging",
            lifecycleState: LIFECYCLE_STATES.CHARGING,
            startTime: evt.ts ?? new Date().toISOString(),
            ts: evt.ts ?? prev.ts ?? null,
        };

        case "STOP_TRANSACTION":
        case "StopTransaction": {
        // ============================================================
        // Freeze the live session state, capture meter_stop, and let
        // (meter_stop - meter_start) drive the receipt fallback in
        // getFinalSessionEnergy when the backend hasn't yet finalized.
        //
        // UNIT FIX: OCPP 1.6 StopTransaction.meterStop is always in Wh.
        // meter_start is stored in kWh, so meter_stop must be normalised
        // the same way (mirrors the StartTransaction handler above).
        // ============================================================
        const meterStopRaw = evt.meter_stop;
        const meterStopKwh = (meterStopRaw !== null && meterStopRaw !== undefined && Number.isFinite(Number(meterStopRaw)))
            ? Number(meterStopRaw) / 1000   // Wh → kWh
            : null;

        // Prefer the freshly-arrived meter_stop, but never let it shrink
        // below a value the live MeterValues path already established.
        const prevCumulative = Number(prev.cumulativeMeter) || 0;
        const resolvedMeterStop = meterStopKwh != null
            ? Math.max(meterStopKwh, prevCumulative, Number(prev.meter_stop) || 0)
            : (prev.meter_stop != null ? Number(prev.meter_stop) : (prevCumulative > 0 ? prevCumulative : null));

        // If we can compute a meter delta now, use it to back-fill
        // energyKWh so the receipt path has something positive even when
        // the live stream missed late frames.
        const meterStart = Number(prev.meter_start);
        const derivedFinalKwh = (resolvedMeterStop != null && Number.isFinite(meterStart))
            ? Math.max(0, resolvedMeterStop - meterStart)
            : null;
        const prevEnergy = Number(prev.energyKWh) || 0;
        const nextEnergy = derivedFinalKwh != null
            ? Math.max(prevEnergy, derivedFinalKwh)
            : prevEnergy;

        console.log('[SessionContext] 🛑 StopTransaction', {
            tx_id: prev.tx_id,
            meter_stop_raw_wh: meterStopRaw !== null && meterStopRaw !== undefined ? meterStopRaw : 'NOT_SET',
            meter_stop_kwh: meterStopKwh != null ? meterStopKwh.toFixed(3) : 'NOT_SET',
            resolvedMeterStop: resolvedMeterStop != null ? resolvedMeterStop.toFixed(3) : null,
            derivedFinalKwh: derivedFinalKwh != null ? derivedFinalKwh.toFixed(3) : null,
            prevEnergy: prevEnergy.toFixed(3),
            nextEnergy: nextEnergy.toFixed(3),
            ts: evt.ts
        });

        return {
            ...prev,
            tx_id: null,  // Clear transaction ID; the late-MeterValues path
                          // above no longer relies on this to accept frames.
            status: "Available",
            lifecycleState: LIFECYCLE_STATES.ENDED,
            endTime: evt.ts ?? new Date().toISOString(),
            ts: evt.ts ?? prev.ts ?? null,
            meter_stop: resolvedMeterStop,
            energyKWh: nextEnergy,
            // Clear any lingering idle flag — the session has ended, so
            // the banner shouldn't persist into the receipt step.
            isIdle: false,
            idleSince: null,
        };
        }

        default:
        return prev;
    }
}

function load() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultState;
        const s = JSON.parse(raw);

        const age = Date.now() - (s.updatedAt || 0);

        // ============================================================
        // SMART TTL: Active charging sessions get 24-hour TTL
        // Pre-charging sessions keep the standard 30-minute TTL
        // This prevents active sessions from being wiped by short browser exits
        // ============================================================
        const isActiveChargingPhase = s.sessionUid && (
            s.step >= STEPS.CONNECT_CHARGER ||
            s.live?.startTime ||
            s.live?.tx_id ||
            s.live?.status === 'Charging' ||
            s.live?.status === 'Preparing' ||
            s.live?.hasReceivedMeterValues === true ||
            s.live?.lifecycleState === LIFECYCLE_STATES.CHARGING
        );

        // A driver who left for /auth is not idle — they are signing up. The
        // passwordless flow is email → 6-digit OTP → (new users) first/last/
        // phone, done on a phone at a charger, and it can outrun 30 minutes.
        // Expiring mid-signup drops stationId, connectorId and step, so they
        // return to a blank wizard with the cable already plugged in.
        //
        // Bounded and purpose-specific rather than a longer global TTL: a
        // genuinely abandoned wizard should still lapse at 30 minutes, or
        // stale station/connector state lingers for everyone.
        const inAuthRoundTrip =
            !!s.authRoundTripStartedAt &&
            Date.now() - s.authRoundTripStartedAt <= AUTH_ROUND_TRIP_TTL_MS;

        const effectiveTTL = isActiveChargingPhase
            ? 24 * 60 * 60 * 1000   // 24 hours for active/charging sessions
            : inAuthRoundTrip
                ? AUTH_ROUND_TRIP_TTL_MS
                : SESSION_TTL_MS;    // 30 minutes for pre-charging sessions

        if (age > effectiveTTL || s.status === "paid") return defaultState;

        const restored = { ...defaultState, ...s };
        // An authorization cannot survive a page load: whatever was waiting
        // on it is gone. Restoring 'authorizing' left the card step on a
        // disabled "Authorizing…" button with Cancel disabled, forever
        // (2026-10-13 stall). Drop the in-flight marker and have the payment
        // step ask the server what happened (usePayments.reconcileAuthorization).
        if (restored.payment?.status === 'authorizing') {
            restored.payment = { ...restored.payment, status: null, needsReconcile: true };
            restored.threeds = null;
        }
        if (restored.paymentStep === PAYMENT_STEPS.PROCESSING) {
            restored.paymentStep = PAYMENT_STEPS.DETAILS;
        }
        // Account-choice rollout flag OFF: drop anything a flag-ON build saved
        // for that screen, so switching the flag between loads leaves no
        // stale answer, pending routing, or a step nobody can see.
        if (!ACCOUNT_CHOICE_ENABLED) {
            restored.accountChoice = null;
            restored.pendingConnectorRouting = null;
            if (restored.step === STEPS.ACCOUNT_CHOICE) restored.step = STEPS.EV_PROFILE;
        }
        return restored;
    } catch {
        return defaultState;
    }
}

function save(s) {
    try {
        // Save state but exclude the raw meter_value array to keep localStorage lean
        // We only need the parsed energyKWh and powerKW values.
        // Also strip the ephemeral `authFailed` flag so a page reload
        // starts clean — the interceptor sets it again if the guard
        // rejects the next request.
        const { live, authFailed: _af, authFailedReason: _afr, ...rest } = s;
        const cleanLive = live ? {
            status: live.status,
            lifecycleState: live.lifecycleState,
            hasReceivedMeterValues: live.hasReceivedMeterValues,
            lastMeterValueAt: live.lastMeterValueAt,
            lastEnergyIncreaseAt: live.lastEnergyIncreaseAt,
            batteryFullSince: live.batteryFullSince,
            meter_start: live.meter_start,  // Session delta baseline
            meter_stop: live.meter_stop,    // Set by StopTransaction (kWh)
            cumulativeMeter: live.cumulativeMeter, // Latest raw meter (kWh)
            energyKWh: live.energyKWh,
            powerKW: live.powerKW,
            tx_id: live.tx_id,
            ts: live.ts,
            startTime: live.startTime,
            endTime: live.endTime,
            // Exclude meter_value array - it's just for debugging
        } : defaultState.live;
        
        const stateToSave = {
            ...rest,
            live: cleanLive
        };
        
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch {}
}

export function SessionProvider({ children }) {
    const [state, setState] = useState(() => load());
    const stateRef = useRef(state);
    // Bootstrap guard: prevents URL sync from clearing sessionUid before recovery logic runs
    const bootstrapCompleteRef = useRef(false);
    
    // Keep ref in sync with state
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => { save(state); }, [state]);

    // Clear the stop-in-flight sessionStorage flag once StopTransaction is confirmed
    // (live.endTime is set by the StopTransaction applyEvent handler)
    useEffect(() => {
        if (state.live?.endTime && state.sessionUid) {
            const key = `ev.stop.inFlight.${state.sessionUid}`;
            if (sessionStorage.getItem(key)) {
                sessionStorage.removeItem(key);
                console.log('[SessionContext] Cleared stop-in-flight flag for', state.sessionUid);
            }
        }
    }, [state.live?.endTime, state.sessionUid]);

    // Sync sessionUid to URL query param for deep-linking and server-authoritative rehydration
    // CRITICAL: Do NOT clear URL sessionUid before bootstrap completes — the URL may be the
    // only recovery signal available (e.g., Safari restoring a tab after memory purge)
    useEffect(() => {
        const url = new URL(window.location);
        const currentUrlUid = url.searchParams.get('sessionUid');
        if (state.sessionUid && state.sessionUid !== currentUrlUid) {
            url.searchParams.set('sessionUid', state.sessionUid);
            window.history.replaceState({}, '', url);
        } else if (!state.sessionUid && currentUrlUid) {
            // Only clear URL after bootstrap is complete — before that, recovery hasn't run yet
            if (bootstrapCompleteRef.current) {
                url.searchParams.delete('sessionUid');
                window.history.replaceState({}, '', url);
            }
        }
    }, [state.sessionUid]);

    // Auto-save active session tracking to localStorage for recovery on cold boot
    // Persists when session enters active phase (CONNECT_CHARGER and beyond)
    useEffect(() => {
        if (state.sessionUid && state.stationId && state.step >= STEPS.CONNECT_CHARGER) {
            saveActiveSession({
                sessionUid: state.sessionUid,
                stationId: state.stationId,
                // May be null for legacy sessions rehydrated from a
                // server row that predates strict connector tracking.
                // Persist as-is; ChargeMonitoring renders a legacy /
                // unknown label rather than lying with "Connector 1".
                connectorId: state.connectorId,
                startedAt: state.live?.startTime || new Date().toISOString(),
                createdAt: new Date().toISOString()
            });
        }
    }, [state.sessionUid, state.stationId, state.step]);

    const esRef = useRef(null);

    const setStep = useCallback((n) => {
        const newStep = Math.max(1, n);
        console.log(`[SessionContext] MAIN step transition: ${stateRef.current.step} -> ${newStep}`, {
            stepNames: {
                from: Object.keys(STEPS).find(k => STEPS[k] === stateRef.current.step),
                to: Object.keys(STEPS).find(k => STEPS[k] === newStep)
            },
            MAKE_PAYMENT_value: STEPS.MAKE_PAYMENT,
            CONNECT_CHARGER_value: STEPS.CONNECT_CHARGER
        });
        setState(s => ({ ...s, step: newStep, updatedAt: Date.now() }));
    }, []);

    const setAmount = useCallback((amountCents, currency) => {
        const cents = Math.round(Number(amountCents) || 0);
        const clamped = Math.max(0, Math.min(cents, 10000000));
        setState(s => ({ ...s, amountCents: clamped, currency: currency || s.currency, updatedAt: Date.now() }));
    }, []);

    const update = useCallback((patch) => {
        setState(s => ({ ...s, ...patch, updatedAt: Date.now() }));
    }, []);

    // The backend's access outcome for the FLEET_ACCESS_RESULT step; null
    // clears it. Cleared by reset() (defaultState) and prepareForNewSession().
    const setAccessOutcome = useCallback((accessOutcome) => {
        setState(s => ({
            ...s,
            accessOutcome: accessOutcome || null,
            // The flag describes the outcome on screen; it goes when that does.
            ...(accessOutcome ? {} : { accessOutcomeSessionHasVehicle: false }),
            updatedAt: Date.now(),
        }));
    }, []);

    /**
     * Mark that the driver is leaving for /auth and will come back. Extends
     * the pre-charging TTL for that trip only (see load()). Cleared on return
     * by the AccountChoice step once authentication resolves.
     */
    const markAuthRoundTrip = useCallback(() => {
        setState(s => ({ ...s, authRoundTripStartedAt: Date.now(), updatedAt: Date.now() }));
    }, []);

    const clearAuthRoundTrip = useCallback(() => {
        setState(s => (s.authRoundTripStartedAt
            ? { ...s, authRoundTripStartedAt: null, updatedAt: Date.now() }
            : s));
    }, []);

    // ── SESSION_AUTH_REQUIRED containment ────────────────────────────
    // Flipped once per session by the shared axios interceptor when the
    // guest-session guard refuses the recovery cookie. While true, the
    // App shell renders <AuthRequiredView /> instead of the current
    // step — that unmounts every polling loop so nothing keeps firing
    // failing requests. Does NOT touch sessionUid or localStorage; the
    // failing session's UID is preserved so operators / OTP recovery
    // can still find it.
    const markAuthFailed = useCallback((reason = null) => {
        setState(s => (
            s.authFailed
                ? s
                : { ...s, authFailed: true, authFailedReason: reason, updatedAt: Date.now() }
        ));
    }, []);

    const clearAuthFailed = useCallback(() => {
        setState(s => (
            s.authFailed
                ? { ...s, authFailed: false, authFailedReason: null, updatedAt: Date.now() }
                : s
        ));
    }, []);

    // Install exactly one axios response interceptor for the duration of
    // the provider. Ejected on unmount so hot-reloads don't stack.
    const markAuthFailedRef = useRef(markAuthFailed);
    markAuthFailedRef.current = markAuthFailed;
    useEffect(() => {
        const apiBase = import.meta.env.VITE_API_URL || '';
        const eject = installAuthFailedInterceptor({
            apiBase,
            onAuthFailed: (info) => markAuthFailedRef.current(info?.correlationId || null),
        });
        return eject;
    }, []);

    // ---- station helpers ----
    const setStationId = useCallback((stationId) => {
        setState(s => ({ ...s, stationId, updatedAt: Date.now() }));
    }, []);

    // Physical OCPP connector id the driver has selected on the
    // charger detail screen. EvProfile calls this after (a) auto-
    // selecting the sole connector on a single-connector charger, or
    // (b) the driver tapping a row in the multi-connector picker.
    // Accepts `null` explicitly so a fresh flow can clear a stale
    // selection when the customer navigates back to pick a different
    // charger.
    const setConnectorId = useCallback((connectorId) => {
        if (connectorId === null || connectorId === undefined) {
            setState(s => ({ ...s, connectorId: null, updatedAt: Date.now() }));
            return;
        }
        const parsed = typeof connectorId === 'number'
            ? connectorId
            : parseInt(String(connectorId), 10);
        if (!Number.isInteger(parsed) || parsed < 1) {
            console.warn('[SessionContext] setConnectorId ignored — invalid value', connectorId);
            return;
        }
        setState(s => ({ ...s, connectorId: parsed, updatedAt: Date.now() }));
    }, []);

    const setPublicId = useCallback((publicId) => {
        setState(s => ({ ...s, publicId, updatedAt: Date.now() }));
    }, []);

    const setStationMeta = useCallback((meta /* {name, location, connector, maxPower, photo} */) => {
        setState(s => ({ ...s, stationMeta: meta, updatedAt: Date.now() }));
    }, []);

    const setStationData = useCallback((stationData) => {
        if (!stationData) return;

        const stationStatus = stationData?.station_status?.[0] ?? stationData?.station_status ?? null;
        setState(s => ({ ...s, stationData, stationStatus, updatedAt: Date.now() }));
    }, []);

    const clearStation = useCallback(() => {
        setState(s => ({
            ...s,
            stationId: null,
            publicId: null,
            stationData: null,
            stationStatus: null,
            stationMeta: null,
            // Also clear the physical connector selection so a fresh
            // charger scan doesn't inherit a stale connectorId from a
            // previous session against a different (or the same
            // multi-connector) charger.
            connectorId: null,
            live: { ...defaultState.live },
            updatedAt: Date.now()
        }));
    }, []);

    const setDebugMode = useCallback((debugMode) => {
        setState(s => ({ ...s, debugMode, updatedAt: Date.now() }));
    }, []);

    const setPaymentStep = useCallback((paymentStep) => {
        console.log(`[SessionContext]  Payment substep transition: ${stateRef.current.paymentStep} → ${paymentStep}`);
        setState(s => ({ ...s, paymentStep, updatedAt: Date.now() }));
    }, []);

    /**
     * Comprehensive session cleanup
     * Clears ALL session-related state, persistence, and active tracking
     * Called when:
     * - User clicks "Start New Session"
     * - User returns to start screen
     * - Session becomes invalid/stale
     * - Rehydration detects terminal/completed session
     */
    const reset = useCallback(() => {
        console.log('[SessionContext] 🧹 Performing comprehensive session cleanup');

        // Capture the previous session uid before we wipe state so we can
        // clear its per-session sessionStorage flags (start-called, stop-in-flight).
        const prevUid = stateRef.current?.sessionUid;

        // DIAGNOSTIC: every reset() call — whether from cancelSession,
        // rehydrateSession's terminal/stale branches, or anywhere else —
        // wipes local session state and *looks* like a cancel to the user
        // even when the backend session is untouched. The stack trace
        // identifies which branch fired this particular reset.
        const stateNow = stateRef.current;
        console.log('[SESSION_CANCEL_TRACE]', {
            site: 'SessionContext.reset',
            sessionUid: prevUid,
            step: stateNow?.step,
            liveStatus: stateNow?.live?.status,
            txId: stateNow?.live?.tx_id ?? null,
            hasReceivedMeterValues: stateNow?.live?.hasReceivedMeterValues ?? null,
            energyKWh: stateNow?.live?.energyKWh ?? null,
            paymentStatus: stateNow?.payment?.status ?? null,
            note: 'local-only reset (does NOT call backend /cancel)',
            ts: new Date().toISOString(),
            trace: (new Error().stack || '').split('\n').slice(2, 8).join(' | ')
        });

        // 1. Reset in-memory state to default
        const fresh = { ...defaultState, updatedAt: Date.now() };
        setState(fresh);
        save(fresh);

        // 2. Clear all session-related localStorage keys
        try {
            // Main session state
            localStorage.removeItem('ev.session.v1');

            // Active session tracking (for rehydration)
            localStorage.removeItem('ev_active_session_uid');
            localStorage.removeItem('ev:lastActiveSession'); // Legacy key
            localStorage.removeItem('activeSessionUid'); // Legacy key

            console.log('[SessionContext] ✓ Cleared session state and tracking keys');
        } catch (error) {
            console.error('[SessionContext] Failed to clear session keys:', error);
        }

        // 2b. Clear the per-session start/stop flags so the next session can
        // legitimately fire POST /start again.
        try {
            if (prevUid) {
                sessionStorage.removeItem(`ev.start.called.${prevUid}`);
                sessionStorage.removeItem(`ev.stop.inFlight.${prevUid}`);
            }
        } catch { /* ignore */ }
        
        // 3. Clear session-scoped persistence (but preserve global data)
        // Note: We don't clear receipt storage or capture flags here because:
        // - Receipt storage is useful for historical lookups
        // - Capture flags prevent duplicate charges (safety feature)
        // These are keyed by sessionUid so won't interfere with new sessions
        
        // 4. SSE connection is already managed by useEffect - will close automatically
        // when stationId is cleared (happens via state reset above)
        
        console.log('[SessionContext] 🧹 Session cleanup complete');
    }, []);

    /**
     * prepareForNewSession — narrow, targeted reset of the payment
     * decision and vehicle trust markers that must NOT carry into a
     * genuinely new charging flow.
     *
     * Called at exactly one boundary: ChargeSetup mount when
     * `state.sessionUid === null` (i.e., before session creation and
     * before vehicle selection/verification for the new flow). Never
     * touches station/charger data required for the current flow,
     * user authentication, saved vehicle records, sessionUid itself,
     * or the wizard step — active-session rehydration is unaffected
     * because rehydration always populates sessionUid before
     * ChargeSetup can mount fresh.
     *
     * The intent is to fail-closed on stale trust markers such as
     * `payment.authorizationId`, `paymentStep === 'receipt'`, or
     * `vehicle.verified: true` + `paymentRequired: false` from a
     * prior FLEET/FREE session. Combined with the fail-closed
     * `shouldShowPaymentStep`, this guarantees a paid session cannot
     * silently bypass MAKE_PAYMENT.
     */
    const prepareForNewSession = useCallback(() => {
        console.log('[SessionContext] 🧼 prepareForNewSession — clearing stale payment + vehicle trust markers');
        setState(s => ({
            ...s,
            // Payment decision + auth state
            payment: { ...defaultState.payment },
            paymentStep: defaultState.paymentStep,
            appliedCredit: null,
            amountCents: 0,
            authorizedAmount: null,
            sessionBudgetCents: null,
            // The previous session's authorization never applies to the next.
            authorization: null,
            // Nor does an access outcome shown for a previous vehicle.
            accessOutcome: null,
            accessOutcomeSessionHasVehicle: false,
            // Saved-vehicle selection (must be reapplied deliberately)
            selectedVehicleId: null,
            selectedVehicle: null,
            // Vehicle trust markers — plate strings are preserved so
            // the manual-plate field can still be prefilled if the
            // user chooses that path; only the verification / billing
            // rules that gate payment are cleared.
            vehicle: {
                ...(s.vehicle || defaultState.vehicle),
                vehicleId: null,
                verified: false,
                billingMode: null,
                rateOverride: null,
                paymentRequired: true,
                rateApplied: null,
                ownerName: null,
                ownerEmail: null,
            },
            // Legacy top-level fields that mirror vehicle trust
            vehicleVerified: false,
            verifiedPlate: null,
            vehicleId: null,
            billingMode: null,
            rateOverride: null,
            // Live-charging evidence from a prior session. These are
            // persisted to localStorage by `save()` and rehydrated on
            // next boot; without this reset, `shouldShowChargingScreen`
            // and `usePreparingNotification` — which read `state.live.*`
            // WITHOUT binding to the current `sessionUid` — would
            // immediately advance the fresh flow from CONNECT_CHARGER
            // to MONITOR_CHARGING on the strength of stale evidence
            // (hasReceivedMeterValues, tx_id + startTime, energyKWh,
            // status='Charging', lifecycleState='CHARGING', …).
            //
            // Safe because this reset ONLY runs when state.sessionUid
            // is null (guarded at the single call site in ChargeSetup),
            // which active-session rehydration never satisfies —
            // rehydration always writes sessionUid before ChargeSetup
            // can mount fresh.
            live: { ...defaultState.live },
            // The next driver answers for themselves. Carrying this over would
            // silently apply one person's guest/account decision to the next.
            accountChoice: null,
            authRoundTripStartedAt: null,
            updatedAt: Date.now(),
        }));
    }, []);

    /**
     * ensureCustomerSession — reusable "session must exist by now"
     * boundary. Returns the existing session UID when one is already
     * in state, and otherwise creates exactly one via
     * `createCustomerSession()` from the current station / pricing
     * state, writing sessionUid + pricingSnapshot + sessionBudgetCents
     * into the context on success.
     *
     * Concurrency: if two callers hit this simultaneously (rapid
     * double-click / two effects firing on the same tick), the second
     * one awaits the first via `ensureRef`. Only ONE POST /api/ev/sessions
     * is issued per successful ensure cycle. On failure the ref is
     * cleared so a retry (Check-again, another click) can proceed.
     *
     * The saved-vehicle click path introduced by commit 5636c4a deferred
     * session creation to "downstream steps" but no downstream step
     * actually created one. This helper is the missing boundary:
     * VehicleVerification calls it after PIN success so both the FREE /
     * FLEET jump-to-CONNECT_CHARGER path AND the paid jump-to-
     * AMOUNT_PICKER / MAKE_PAYMENT path leave with a real sessionUid.
     */
    const ensureRef = useRef(null);
    const ensureCustomerSession = useCallback(async (opts = {}) => {
        // Callers may pass `{ vehiclePin }` when they've just verified a
        // vehicle PIN and need it forwarded to POST /api/sessions so the
        // backend can re-bind PIN → specific vehicleId in the guest
        // flow. Never read the PIN from persistent state — the caller
        // holds it in memory only.
        const { vehiclePin: vehiclePinOverride = null } = opts;
        // The vehicle PIN verification just proved. Passed explicitly because
        // the caller's update({ vehicle }) has not reached stateRef yet: read
        // from state, the session was created with the typed plate only and
        // no vehicle_id, so nothing downstream (billing entitlement, owner
        // receipt) knew which vehicle was charging. `null` means "attach no
        // vehicle"; omitted means "resolve from state as before".
        const hasVehicleOverride = Object.prototype.hasOwnProperty.call(opts, 'vehicleId');
        const vehicleIdOverride = hasVehicleOverride ? (opts.vehicleId || null) : undefined;
        const existingUid = stateRef.current?.sessionUid;
        if (existingUid) {
            return { ok: true, sessionUid: existingUid, alreadyExisted: true };
        }
        if (ensureRef.current) {
            // Another caller has a create in flight — join the wait so
            // the second click / effect never fires a duplicate POST.
            return ensureRef.current;
        }

        const promise = (async () => {
            const s = stateRef.current || {};
            const stationMeta = s.stationMeta || {};
            if (!s.stationId) {
                return {
                    ok: false,
                    error: 'STATION_ID_MISSING',
                    message: 'Cannot start charging: station information is missing. Please return to the setup step.',
                };
            }
            const pricingSnapshot = {
                rate_per_kwh: stationMeta.ratePerKwh,
                rate_per_minute: stationMeta.ratePerMinute || null,
                billing_unit: stationMeta.billingUnit || 'kwh',
                session_fee: stationMeta.sessionFee || 0,
                currency: s.currency || 'BBD',
                min_charge_amount: stationMeta.minChargeAmount || 0,
            };

            // Plate attribution — same resolution rule the ChargeSetup
            // Continue button applies. Reads from the same session
            // state so a wizard-late createCustomerSession (e.g. from
            // ChargeMonitoring recovery) uses whatever the driver
            // picked in earlier steps.
            const _vehicleId = hasVehicleOverride
                ? vehicleIdOverride
                : (s.selectedVehicleId || s.vehicle?.vehicleId || null);
            const _typedPlate = s.vehicle?.licensePlateRaw || s.licensePlate || null;
            // connectorId is REQUIRED. The EvProfile picker gates
            // Get Started on selection (auto-selecting for single-
            // connector chargers), so by the time we reach
            // ensureCustomerSession `s.connectorId` should be a real
            // physical connector id. If it's null the API-layer guard
            // in createCustomerSession will refuse and surface
            // INVALID_CONNECTOR_ID rather than mis-routing the session.
            const result = await createCustomerSession({
                stationId: s.stationId,
                connectorId: s.connectorId,
                pricingSnapshot,
                redeemCode: s.appliedCredit ? s.appliedCredit.code : null,
                vehicleId: _vehicleId,
                licensePlate: _vehicleId ? null : _typedPlate,
                // A PIN-verified vehicle came from a typed plate, not a saved
                // pick, so its capture source says so.
                plateCaptureSource: _vehicleId
                    ? (hasVehicleOverride && _typedPlate ? 'manual_entry' : 'saved_vehicle')
                    : (_typedPlate ? 'manual_entry' : null),
                // Forward the just-verified guest PIN when the caller
                // hands it in. Never sourced from state (which could
                // outlive the verify step) — only from the immediate
                // caller.
                vehiclePin: vehiclePinOverride,
            });

            if (!result?.sessionUid) {
                return {
                    ok: false,
                    error: result?.error || 'SESSION_CREATE_FAILED',
                    message: result?.message || 'We could not start this charging session. Please try again.',
                    // P4b AUTHORIZATION_DENIED detail (driver copy is in message).
                    ...(result?.reasonCode !== undefined ? { reasonCode: result.reasonCode } : {}),
                    ...(result?.offerCard !== undefined ? { offerCard: result.offerCard } : {}),
                    ...(result?.authorization !== undefined ? { authorization: result.authorization } : {}),
                    // Phase 3: the access outcome a charger-access refusal carries.
                    ...(result?.accessOutcome ? { accessOutcome: result.accessOutcome } : {}),
                };
            }
            // The pricing to display: the server tariff when the API applied
            // one (enforce), else the snapshot sent (still sent for older API
            // builds — plan v3.1 §21 P4 compatibility).
            const displayPricing = result.pricingSnapshot || pricingSnapshot;

            // Session-budget mirrors the ChargeSetup Continue path exactly
            // so downstream ETA / budget-cap logic sees consistent state
            // regardless of which entry point created the session.
            let sessionBudgetCents;
            if (s.authorizedAmount && s.authorizedAmount > 0) {
                sessionBudgetCents = s.authorizedAmount;
            } else {
                const minChargeAmountCents = Math.round((displayPricing.min_charge_amount || 0) * 100);
                sessionBudgetCents = minChargeAmountCents > 0 ? minChargeAmountCents : 1000;
            }

            try {
                localStorage.setItem('activeSessionUid', result.sessionUid);
            } catch { /* ignore */ }

            setState((prev) => ({
                ...prev,
                sessionUid: result.sessionUid,
                sessionId: result.sessionUid,
                sessionStatus: result.session?.status,
                pricingSnapshot: displayPricing,
                authorization: result.authorization ?? null,
                sessionBudgetCents,
                updatedAt: Date.now(),
            }));

            return {
                ok: true,
                sessionUid: result.sessionUid,
                alreadyExisted: false,
                authorization: result.authorization ?? null,
            };
        })();

        ensureRef.current = promise;
        try {
            const out = await promise;
            return out;
        } finally {
            // Clear the ref so a genuine future call (e.g. after an
            // earlier failure) can start fresh. On success this is
            // still fine — the state guard at the top short-circuits.
            ensureRef.current = null;
        }
    }, []);

    /**
     * Centralized SSE event handler
     * All SSE events flow through this function to update session state
     * 
     * NORMALIZATION LAYER: Real chargers send OCPP raw events with different shape
     * than simulators. Normalize to consistent frontend shape before processing.
     */
    const handleSSEEvent = useCallback((data, backendSession = null) => {
        if (!data) {
            console.warn('[SessionContext] Invalid SSE event - no data');
            return;
        }

        // ============================================================
        // EVENT NORMALIZATION: Handle both simulator and real charger shapes
        // Simulator:     { type, meter_value, tx_id, ... }
        // Real charger:  { event_name, payload: { meter_value, transaction_id, ... }, ... }
        // ============================================================
        const normalized = {
            ...data,
            // Map event_name → type (real charger compatibility)
            type: data.type || data.event_name || null,
            // Flatten payload fields to top level (real charger compatibility)
            meter_value: data.meter_value ?? data.payload?.meter_value,
            meter_start: data.meter_start ?? data.payload?.meter_start,
            meter_stop: data.meter_stop ?? data.payload?.meter_stop,
            tx_id: data.tx_id ?? data.payload?.transaction_id ?? null,
            connector_id: data.connector_id ?? data.payload?.connector_id ?? null,
            station_id: data.station_id ?? data.payload?.station_id ?? null,
            status: data.status ?? data.payload?.status ?? null,
            ts: data.ts ?? null,
        };

        if (!normalized.type) {
            console.warn('[SessionContext] Invalid SSE event - missing type/event_name:', {
                hasType: !!data.type,
                hasEventName: !!data.event_name,
                keys: Object.keys(data)
            });
            return;
        }

        console.log('[SessionContext] SSE event:', normalized.type);

        // Update state with the normalized event using the applyEvent reducer
        // Pass backend session for authoritative recovery scenarios
        setState(s => ({
            ...s,
            live: applyEvent(s.live, normalized, backendSession),
            updatedAt: Date.now()
        }));
    }, []);


    const pollingRef = useRef(null);

    /**
     * SSE Connection Management — session-scoped stream.
     *
     * The customer live-stream endpoint is `/api/stream/stream/sessions/:uid`
     * (see sw-ev-api/routes/stream.js). It requires either a ticket, a
     * bearer JWT + owned session, or the guest recovery cookie. Native
     * EventSource cannot attach an Authorization header, so we mint a
     * fresh single-use ticket from POST /api/stream/tickets before every
     * connection (and every reconnect). The prior implementation used
     * the admin-only `/api/stream/stream?stationId=…` endpoint and
     * received `STREAM_TICKET_REQUIRED` 401s for every customer.
     *
     * Falls back to 3s polling when:
     *   - ticket mint fails with a terminal auth code (401/403/404) —
     *     the caller cannot obtain SSE credentials, don't retry;
     *   - SSE fully disconnects (readyState === 2).
     */
    useEffect(() => {
        if (!state.sessionUid) {
            // No sessionUid — close any existing connection and polling
            if (esRef.current) {
                console.log('[SessionContext] Closing SSE connection - no sessionUid');
                esRef.current.close();
                esRef.current = null;
            }
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            return;
        }

        const startPollingFallback = () => {
            if (pollingRef.current) return; // already polling
            const currentUid = stateRef.current.sessionUid;
            if (!currentUid) return; // no session to poll

            console.log('[SessionContext] SSE closed, starting 3s polling fallback');
            pollingRef.current = setInterval(async () => {
                const uid = stateRef.current.sessionUid;
                if (!uid) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    return;
                }
                try {
                    const result = await getCustomerSession(uid);
                    if (result.ok && result.session) {
                        const s = result.session;
                        // Feed poll results into state — but NEVER downgrade
                        // a session that has firsthand MeterValues evidence.
                        // The backend often lags behind the SSE stream and
                        // would otherwise stomp Charging back to Preparing.
                        // ============================================================
                        // CRITICAL FIX: Use || (not ??) for energyKWh to prevent
                        // backend zero values from overwriting locally-accumulated
                        // energy. Before finalization, backend may return energy_kwh=0
                        // which ?? treats as valid, destroying the real accumulated value.
                        // ============================================================
                        setState(prev => {
                            const meterActive = hasActiveMeterActivity(prev.live);
                            const backendTerminal = isBackendTerminalStatus(s.status);
                            const normalizedBackend = normalizeOcppStatus(s.status);

                            // If polling says terminal, honour it (session really ended)
                            // even when meter was previously active.
                            const statusToWrite = backendTerminal
                                ? (normalizedBackend || prev.live?.status)
                                : (meterActive
                                    ? prev.live?.status  // keep local — don't downgrade
                                    : (normalizedBackend || prev.live?.status));

                            if (meterActive && !backendTerminal) {
                                console.log(
                                    '[SessionContext] Ignoring stale preparing poll because MeterValues already active',
                                    { backendStatus: normalizedBackend, keptStatus: prev.live?.status, hasReceivedMeterValues: prev.live?.hasReceivedMeterValues }
                                );
                            }

                            const nextLive = {
                                ...prev.live,
                                status: statusToWrite,
                                energyKWh: s.energy_kwh || s.final_energy_kwh || prev.live?.energyKWh || 0,
                                endTime: s.stopped_at || prev.live?.endTime,
                            };
                            // Cold-load / SSE-gap correctness: seed the
                            // idle + battery-full signals from the DTO so
                            // the "Charging paused" / "Charging complete"
                            // banners can render immediately without
                            // waiting for the next SSE frame. Never
                            // silently downgrade a live-progressing
                            // session — if the local meter shows fresh
                            // activity, treat the DTO snapshot as stale
                            // and drop the paused/complete signals.
                            if (meterActive) {
                                nextLive.idleSince = null;
                                nextLive.batteryFullSince = null;
                                nextLive.chargingActivity = 'CHARGING';
                            } else {
                                nextLive.idleSince = s.idle?.idleSince ?? prev.live?.idleSince ?? null;
                                nextLive.batteryFullSince = s.idle?.batteryFullSince ?? prev.live?.batteryFullSince ?? null;
                                nextLive.chargingActivity = s.chargingActivity ?? prev.live?.chargingActivity ?? null;
                                nextLive.isIdle = !!nextLive.idleSince;
                            }
                            nextLive.lifecycleState = deriveLifecycleState({
                                live: nextLive,
                                backendSession: s,
                            });
                            return {
                                ...prev,
                                live: nextLive,
                                updatedAt: Date.now(),
                            };
                        });
                    }
                } catch (err) {
                    console.warn('[SessionContext] Polling fallback error:', err.message);
                }
            }, 3000);
        };

        const stopPollingFallback = () => {
            if (pollingRef.current) {
                console.log('[SessionContext] SSE reconnected, stopping polling fallback');
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };

        // Session-scoped stream needs a short-lived ticket. Native
        // EventSource cannot attach an Authorization header, so the
        // mint happens ahead of every connection and every reconnect,
        // then the raw ticket rides along as `?ticket=…` — and never
        // appears in a log line.
        const sessionUid = state.sessionUid;
        const encodedSessionUid = encodeURIComponent(sessionUid);
        // `sseCancelled` flips true on effect cleanup (session change /
        // unmount) so late-resolving mint promises can't reopen a stale
        // stream after we've moved on.
        let sseCancelled = false;
        let reconnectTimer = null;
        // Bounded backoff for network / open-then-error transitions.
        // Terminal auth failures (401/403 on mint) short-circuit this
        // and hand off to the polling fallback permanently.
        let backoffMs = 1000;

        const clearReconnectTimer = () => {
            if (reconnectTimer != null) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
        };

        const scheduleReconnect = () => {
            if (sseCancelled) return;
            clearReconnectTimer();
            reconnectTimer = setTimeout(() => {
                if (sseCancelled) return;
                openStream();
            }, backoffMs);
            backoffMs = Math.min(backoffMs * 2, 30_000);
        };

        // Guests authorise the session stream with their HttpOnly recovery
        // cookie (EventSource withCredentials), NOT a minted ticket. This is
        // the primary live path for a guest — polling stays a recovery-only
        // backup. Signed-in users never reach here; they mint a ticket.
        const openCookieStream = () => {
            console.log('[SessionContext] Opening cookie-based session SSE (guest)');
            const cookieUrl = `${import.meta.env.VITE_API_URL}/stream/stream/sessions/${encodedSessionUid}`;
            const es = new EventSource(cookieUrl, { withCredentials: true });
            esRef.current = es;
            backoffMs = 1000;
            attachHandlers(es);
        };

        const openStream = async () => {
            if (sseCancelled) return;
            // Close any previous EventSource before opening a new one. We
            // only keep ONE active stream per session.
            if (esRef.current) {
                try { esRef.current.close(); } catch { /* ignore */ }
                esRef.current = null;
            }

            // No access token → this is a guest. Do NOT call the ticket-mint
            // endpoint (guests have no JWT and the stream authorises them by
            // their session cookie). Open the cookie stream directly; fall
            // back to polling + retry only if the EventSource itself throws.
            if (!getAccessToken()) {
                try {
                    openCookieStream();
                } catch (cookieErr) {
                    console.warn('[SessionContext] cookie-based SSE failed to open', cookieErr);
                    startPollingFallback();
                    scheduleReconnect();
                }
                return;
            }

            // Signed-in driver: mint a short-lived ticket with their Bearer
            // and open the ticketed stream. The ticket/SSE path is primary.
            const mint = await mintSessionStreamTicket(sessionUid);
            if (sseCancelled) return;

            if (!mint.ok) {
                if (isTerminalMintStatus(mint.status)) {
                    // A signed-in user's mint terminally failed — e.g. the
                    // access token expired mid-session, or this session is
                    // not theirs. Do NOT silently fall back to the guest
                    // cookie path (they have no recovery cookie anyway).
                    // Surface polling to keep the UI alive; the expired-auth
                    // path (a 401 SESSION_AUTH_REQUIRED on the poll) drives
                    // the app's login/recovery UI via authFailedInterceptor.
                    console.warn(
                        '[SessionContext] SSE ticket mint terminal for signed-in user — polling only',
                        { status: mint.status, error: mint.error },
                    );
                    startPollingFallback();
                    return;
                }
                // Transient mint failure. Retry with backoff so we still
                // eventually reconnect on a network blip. The polling
                // fallback also runs so the UI keeps updating meanwhile.
                console.warn(
                    '[SessionContext] SSE ticket mint transient failure — retrying with backoff',
                    { status: mint.status, error: mint.error, backoffMs },
                );
                startPollingFallback();
                scheduleReconnect();
                return;
            }

            // Mint succeeded. Build the URL with the ticket appended.
            // Never log the raw ticket — only that we have one.
            const sseUrl = `${import.meta.env.VITE_API_URL}/stream/stream/sessions/${encodedSessionUid}?ticket=${encodeURIComponent(mint.ticket)}`;
            console.log('[SessionContext] Opening session SSE', {
                sessionUidFp: sessionUid.slice(0, 8),
                hasTicket: true,
            });

            const es = new EventSource(sseUrl);
            esRef.current = es;
            // Reset backoff on a fresh open — successful reconnects
            // start from the low end again.
            backoffMs = 1000;

            attachHandlers(es);
        };

        // SSE sequence tracking for gap detection
        let lastSeq = 0;
        let gapCount = 0;
        // The very first onopen is the initial connect (page load already
        // fetched authoritative state). Every LATER onopen is a reconnect,
        // and any events published while the stream was down are gone — SSE
        // has no replay. So on reconnect we fetch server state once to
        // reconcile whatever we missed (e.g. live energy that advanced during
        // the outage), instead of freezing at the last streamed value.
        let hasConnectedOnce = false;

        // Fetch authoritative session state and fold it into `live`. Used
        // both by the gap-threshold catch-up and by the reconnect handler.
        const reconcileFromServer = () => {
            if (!stateRef.current.sessionUid) return;
            getCustomerSession(stateRef.current.sessionUid).then(result => {
                if (result?.ok && result.session) {
                    const s = result.session;
                    const liveNow = stateRef.current.live;
                    const meterActive = hasActiveMeterActivity(liveNow);
                    const backendTerminal = isBackendTerminalStatus(s.status);
                    const normalizedBackend = normalizeOcppStatus(s.status);
                    const statusToWrite = backendTerminal
                        ? (normalizedBackend || liveNow.status)
                        : (meterActive
                            ? liveNow.status
                            : (normalizedBackend || liveNow.status));
                    if (meterActive && !backendTerminal) {
                        console.log(
                            '[SessionContext] Ignoring stale preparing poll because MeterValues already active',
                            { backendStatus: normalizedBackend, keptStatus: liveNow.status, hasReceivedMeterValues: liveNow.hasReceivedMeterValues }
                        );
                    }
                    // Never regress energy: the server value is authoritative
                    // for anything missed during an outage, but a stream frame
                    // may already have advanced past the poll snapshot.
                    const serverEnergy = s.energy_kwh || s.final_energy_kwh || 0;
                    const nextEnergy = Math.max(
                        Number(serverEnergy) || 0,
                        Number(liveNow.energyKWh) || 0,
                    );
                    const nextLive = {
                        ...liveNow,
                        status: statusToWrite,
                        energyKWh: nextEnergy || liveNow.energyKWh,
                        endTime: s.stopped_at || liveNow.endTime,
                    };
                    nextLive.lifecycleState = deriveLifecycleState({
                        live: nextLive,
                        backendSession: s,
                    });
                    update({ live: nextLive });
                }
            }).catch(() => {});
        };

        const attachHandlers = (es) => {
            es.onopen = () => {
                console.log('[SessionContext] SSE connected');
                lastSeq = 0; // Reset on reconnect
                gapCount = 0;
                stopPollingFallback();
                // Reconcile after a reconnect — catch up on events lost while
                // the stream was down (SSE has no replay window).
                if (hasConnectedOnce) {
                    console.log('[SessionContext] SSE reconnected — reconciling server state');
                    reconcileFromServer();
                }
                hasConnectedOnce = true;
            };

            es.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                // Charger UX: a versioned canonical snapshot. Ordered by its
                // own `version` (applied only when newer; a duplicate replays
                // nothing), not by the stream's `_seq`. If it would move a
                // finished session back, it is ignored and the authoritative
                // state is fetched instead.
                if (data?.type === 'session.snapshot') {
                    const decision = applySnapshot(data.snapshot);
                    if (decision === DECISION.REGRESSION || decision === DECISION.INVALID) {
                        reconcileFromServer();
                    }
                    return;
                }

                // Gap detection: check sequence continuity
                if (data._seq) {
                    if (lastSeq > 0 && data._seq > lastSeq + 1) {
                        const missed = data._seq - lastSeq - 1;
                        gapCount += missed;
                        console.warn(`[SSE_GAP] Missed ${missed} event(s): expected seq ${lastSeq + 1}, got ${data._seq} (total gaps: ${gapCount})`);
                        // After 3+ gaps, trigger a poll to catch up
                        if (gapCount >= 3 && stateRef.current.sessionUid) {
                            console.warn('[SSE_GAP] Gap threshold reached, fetching server state');
                            reconcileFromServer();
                            gapCount = 0; // Reset after catch-up
                        }
                    }
                    lastSeq = data._seq;
                }

                handleSSEEvent(data);
            } catch (error) {
                console.error('[SessionContext] Failed to parse SSE event:', event.data, error);
            }
        };

            es.onerror = (err) => {
                console.warn('[SessionContext] SSE error', err);
                console.log('[SessionContext] SSE readyState:', es.readyState);
                // Any non-OPEN error means the live stream is not currently
                // delivering — start the polling bridge so the UI keeps
                // advancing during the outage. It is stopped again on the
                // next successful onopen. This matters for the cookie stream:
                // when the API bounces, a cookie EventSource sits in
                // readyState 0 (CONNECTING) retrying itself and NEVER reaches
                // CLOSED, so keying only on readyState 2 left a guest with no
                // recovery bridge at all.
                if (es.readyState !== 1) {
                    startPollingFallback();
                }
                // readyState 2 = CLOSED: native EventSource has given up —
                // e.g. a single-use ticket whose auto-retry got 401. Mint a
                // fresh ticket and reconnect on our own timer. readyState 0 =
                // CONNECTING: native is already retrying (the cookie stream
                // reconnects itself once the API is back), so we must NOT
                // schedule a competing reconnect that would open a 2nd stream.
                if (es.readyState === 2) {
                    scheduleReconnect();
                }
            };
        };

        // Kick off the first connection attempt. This is async but the
        // effect body does not need to await it — openStream manages
        // esRef, backoff and cancellation on its own.
        openStream();

        // Cleanup: close the current SSE connection, cancel any pending
        // reconnect, and stop polling when the sessionUid changes or the
        // component unmounts.
        return () => {
            sseCancelled = true;
            clearReconnectTimer();
            console.log('[SessionContext] Closing SSE connection');
            if (esRef.current) {
                try { esRef.current.close(); } catch { /* ignore */ }
                esRef.current = null;
            }
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [state.sessionUid, handleSSEEvent]);

    /**
     * Cancel session before charging starts
     * Uses unified cancelAndCleanup utility to ensure proper connector release
     * 
     * BEHAVIOR:
     * - If no sessionUid: just resets local state
     * - If sessionUid exists: calls unified cancel utility
     * - If charging started: blocks cancel (must use stop instead)
     * - Logs all attempts with correlationId
     * - Clears local state ONLY AFTER cancel attempt succeeds
     * 
     * @returns {Promise<object>} { success, error?, message?, correlationId? }
     */
    const cancelSession = useCallback(async ({ serverSaysNotStarted = false } = {}) => {
        const currentSessionUid = stateRef.current.sessionUid;
        const currentStationId = stateRef.current.stationId;
        const currentConnectorId = stateRef.current.connectorId;
        const currentLiveStatus = stateRef.current.live?.status;
        const hasTxId = !!stateRef.current.live?.tx_id;

        // DIAGNOSTIC: every invocation of cancelSession (whether it ends up
        // blocked, local-only, or hitting the backend) is traced here so we
        // can attribute the regression to a specific caller.
        const stateNow = stateRef.current;
        console.log('[SESSION_CANCEL_TRACE]', {
            site: 'SessionContext.cancelSession',
            sessionUid: currentSessionUid,
            stationId: currentStationId,
            connectorId: currentConnectorId,
            step: stateNow.step,
            liveStatus: currentLiveStatus,
            txId: stateNow.live?.tx_id ?? null,
            hasReceivedMeterValues: stateNow.live?.hasReceivedMeterValues ?? null,
            energyKWh: stateNow.live?.energyKWh ?? null,
            paymentStatus: stateNow.payment?.status ?? null,
            ts: new Date().toISOString(),
            trace: (new Error().stack || '').split('\n').slice(2, 6).join(' | ')
        });

        // CASE 1: No sessionUid - just reset local state
        if (!currentSessionUid) {
            console.log('[SessionContext] No sessionUid - resetting local state only');
            reset();
            return {
                success: true,
                message: 'Session cleared',
                localOnly: true
            };
        }

        // CASE 2: Check if charging has already started
        // Charging is indicated by live.status being "Charging" or "Preparing" or having tx_id
        // A caller that has just asked the server (useCancelAttempt) passes
        // serverSaysNotStarted: then only a transaction id counts. 'Preparing'
        // only means a cable is plugged in; the server refuses a pre-start
        // cancel of a session that is really charging.
        const chargingStarted = hasTxId || (!serverSaysNotStarted && (
            currentLiveStatus === 'Charging' ||
            currentLiveStatus === 'Preparing'));

        if (chargingStarted) {
            console.log('[SessionContext] Charging already started, cannot cancel (must use stop instead)');
            return {
                success: false,
                error: 'Charging already started — please stop session instead.',
                blockReason: 'charging_started'
            };
        }

        // CASE 3: Call unified cancel utility
        const result = await cancelAndCleanup({
            sessionUid: currentSessionUid,
            reason: 'user_cancel',
            source: 'SessionContext',
            context: {
                stationId: currentStationId,
                connectorId: currentConnectorId,
                // Forward state snapshot so cancelAndCleanup's trace has the
                // same context this site already captured.
                liveStatus: currentLiveStatus,
                txId: stateNow.live?.tx_id ?? null,
                hasReceivedMeterValues: stateNow.live?.hasReceivedMeterValues ?? null,
                energyKWh: stateNow.live?.energyKWh ?? null,
                paymentStatus: stateNow.payment?.status ?? null,
                step: stateNow.step
            },
            onSuccess: () => {
                // Clear local state AFTER successful cancel
                reset();
            },
            onError: (error) => {
                // FAIL-SAFE: Clear local state even on error to prevent UI deadlock
                console.warn('[SessionContext] Clearing local state despite cancel error (fail-safe)');
                reset();
            }
        });

        return result;
    }, [reset]);

    /**
     * Stop charging session
     * Sends OCPP stop command - finalization happens automatically via StopTransaction
     *
     * Duplicate-tab / double-tap guard: uses sessionStorage (tab-scoped) to ensure
     * only one stop command is in-flight at a time per tab.  A second tab opening the
     * same session will have its own sessionStorage and can issue its own stop, but
     * within a single tab a double-tap or React double-render is blocked.
     */
    const stopSession = useCallback(async () => {
        const currentSessionUid = stateRef.current.sessionUid;

        if (!currentSessionUid) {
            console.error('[SessionContext] Cannot stop - no sessionUid');
            return { success: false, error: 'No session to stop' };
        }

        // Duplicate-stop guard (tab-scoped via sessionStorage)
        const stopFlagKey = `ev.stop.inFlight.${currentSessionUid}`;
        if (sessionStorage.getItem(stopFlagKey)) {
            console.warn('[SessionContext] Stop already in progress — ignoring duplicate request');
            return { success: false, error: 'Stop already in progress', duplicate: true };
        }
        sessionStorage.setItem(stopFlagKey, Date.now().toString());

        console.log('[SessionContext] Stopping session:', currentSessionUid);

        try {
            const result = await apiStopSession(currentSessionUid);

            if (!result.ok) {
                // Clear flag on failure so the user can retry
                sessionStorage.removeItem(stopFlagKey);
                console.error('[SessionContext] Stop failed:', result.message);
                return { success: false, error: result.message, correlationId: result.correlationId };
            }

            console.log('[SessionContext] Stop command sent, waiting for StopTransaction...');
            // Flag stays set until StopTransaction arrives (cleared by useEffect below)
            return { success: true, tx_id: result.tx_id, correlationId: result.correlationId };
        } catch (error) {
            sessionStorage.removeItem(stopFlagKey); // allow retry on exception
            console.error('[SessionContext] Exception during stop:', error);
            return { success: false, error: error.message };
        }
    }, []);

    /**
     * Validate and normalize a session object from API response
     * Ensures the session has required fields before using it
     * 
     * @param {object} session - Session object from API
     * @returns {object|null} - Normalized session or null if invalid
     */
    const normalizeSession = (session) => {
        if (!session || typeof session !== 'object') {
            console.warn('[SessionContext] Invalid session - not an object:', session);
            return null;
        }

        // Normalize session_uid vs sessionUid
        const sessionUid = session.sessionUid ?? session.session_uid;
        
        // Required fields validation
        if (!sessionUid || typeof sessionUid !== 'string') {
            console.warn('[SessionContext] Invalid session - missing sessionUid:', {
                keys: Object.keys(session)
            });
            return null;
        }

        if (!session.status || typeof session.status !== 'string') {
            console.warn('[SessionContext] Invalid session - missing status:', {
                sessionUid,
                keys: Object.keys(session)
            });
            return null;
        }

        // Return normalized session with consistent key names
        return {
            ...session,
            sessionUid,  // Ensure sessionUid is always present
            session_uid: sessionUid  // Keep server format too for compatibility
        };
    };

    /**
     * Rehydrate session from server after page refresh or offline/online
     * This ensures the UI recovers to the correct state
     * 
     * @param {string|null} overrideSessionUid - Optional sessionUid override.
     *   Solves the race condition where App.jsx calls update({ sessionUid }) then
     *   rehydrateSession() in the same tick — stateRef hasn't updated yet.
     *   Also used for cold-boot recovery from localStorage.
     */
    const rehydrateSession = useCallback(async (overrideSessionUid = null) => {
        const currentSessionUid = overrideSessionUid || stateRef.current.sessionUid;
        
        if (!currentSessionUid) {
            console.log('[SessionContext] No sessionUid to rehydrate');
            return { success: false, reason: 'no_session_uid' };
        }

        // If override provided and differs from state, set it immediately
        if (overrideSessionUid && overrideSessionUid !== stateRef.current.sessionUid) {
            console.log('[SessionContext] Setting override sessionUid into state:', overrideSessionUid);
            setState(s => ({ ...s, sessionUid: overrideSessionUid, updatedAt: Date.now() }));
        }

        console.log('[SessionContext]  Rehydrating session:', currentSessionUid);

        try {
            const result = await getCustomerSession(currentSessionUid);

            // Log response structure for debugging
            console.log('[SessionContext] API response keys:', {
                ok: result.ok,
                notFound: result.notFound,
                hasSession: !!result.session,
                sessionKeys: result.session ? Object.keys(result.session) : []
            });

            if (result.notFound) {
                console.warn('[SessionContext]  Session not found on server, resetting local state');
                reset();
                return { success: false, reason: 'not_found' };
            }

            if (!result.ok) {
                console.error('[SessionContext]  Failed to rehydrate session:', result.message);
                // IMPORTANT: Do NOT reset on transient network errors during rehydration.
                // The user may have an active charging session and simply lost connectivity.
                // Only reset when we have a definitive server response (notFound, terminal status).
                // Transient errors: keep local state intact so SSE/polling can recover.
                console.warn('[SessionContext] Transient error during rehydration — keeping local state intact');
                return { success: false, reason: 'error', error: result.message, transient: true };
            }

            // Normalize and validate session object
            const session = normalizeSession(result.session);
            
            if (!session) {
                console.error('[SessionContext]  Session validation failed - invalid response structure');
                console.warn('[SessionContext] Clearing invalid session from storage');
                reset();
                return { success: false, reason: 'invalid_session' };
            }

            console.log('[SessionContext]  Session rehydrated:', {
                sessionUid: session.sessionUid,
                status: session.status,
                stationId: session.station_id
            });

            // ============================================================
            // BACKEND-AUTHORITATIVE SESSION RECOVERY
            // When backend says charging, ALWAYS recover to monitoring mode
            // ============================================================
            
            // Determine UI step based on session status
            // IMPORTANT: This mapping ensures deterministic step recovery on refresh/rehydration
            let targetStep = stateRef.current.step; // Default to current step
            let targetPaymentStep = stateRef.current.paymentStep; // Default payment substep
            let recoveredLiveState = stateRef.current.live; // Default to current live state
            
            // ════════════════════════════════════════════════════════════
            // BACKEND STATUS NORMALISATION
            // Backend ships lowercase; treat both cases identically so the
            // switch below doesn't fall into `default` and yell about an
            // "Unknown session status" we already know how to handle.
            // ════════════════════════════════════════════════════════════
            const backendStatusRaw = session.status;
            const backendStatus = String(backendStatusRaw || '').toLowerCase();

            switch (backendStatus) {
                case 'pending':
                    // Session created but not yet paid/started
                    // Check if session is stale (older than a few minutes)
                    const createdAt = new Date(session.created_at || session.createdAt);
                    const ageMinutes = (Date.now() - createdAt.getTime()) / 1000 / 60;
                    
                    if (ageMinutes > 5) {
                        // Stale pending session - release it on the server,
                        // then clear and restart (see releaseStaleAttempt).
                        console.warn('[SessionContext]  Rehydration rejected — stale pending session (age:', ageMinutes.toFixed(1), 'min)');
                        releaseStaleAttempt(session.session_uid || session.sessionUid);
                        reset();
                        return { success: false, reason: 'stale_pending_session', ageMinutes };
                    }
                    
                    // Fresh pending session - allow resumption to CHARGE_SETUP
                    // Only go back to CHARGE_SETUP if we're before payment steps
                    if (stateRef.current.step < STEPS.MAKE_PAYMENT) {
                        targetStep = STEPS.CHARGE_SETUP;
                    }
                    // A card authorization running on this page (the driver
                    // is in the bank's challenge, or switched to the bank app
                    // and back): leave the payment step where it is. Moving
                    // it to DETAILS unmounted the 3DS iframe mid-challenge
                    // and showed "Authorizing…" with Cancel disabled.
                    if (isPreauthActive(currentSessionUid)) {
                        break;
                    }
                    // Reset payment substep to start
                    targetPaymentStep = PAYMENT_STEPS.DETAILS;
                    break;
                
                case 'authorized': {
                    // Payment authorized but not yet started
                    // Stale check: authorized sessions older than 15 minutes are likely abandoned
                    const authCreatedAt = new Date(session.created_at || session.createdAt);
                    const authAgeMinutes = (Date.now() - authCreatedAt.getTime()) / 1000 / 60;

                    if (authAgeMinutes > 15) {
                        console.warn('[SessionContext]  Rehydration rejected — stale authorized session (age:', authAgeMinutes.toFixed(1), 'min)');
                        releaseStaleAttempt(session.session_uid || session.sessionUid);
                        reset();
                        return { success: false, reason: 'stale_authorized_session', ageMinutes: authAgeMinutes };
                    }

                    // CRITICAL: If payment is authorized, user should ALWAYS be at or past CONNECT_CHARGER
                    // Never send them back to payment processing
                    console.log('[SessionContext]  Payment authorized - ensuring step >= CONNECT_CHARGER');

                    if (stateRef.current.step < STEPS.CONNECT_CHARGER) {
                        targetStep = STEPS.CONNECT_CHARGER;
                    } else {
                        targetStep = stateRef.current.step;
                    }

                    targetPaymentStep = PAYMENT_STEPS.DETAILS;
                    break;
                }
                
                case 'preparing':
                case 'remote_start_requested': {
                    // ============================================================
                    // PREPARING / STARTING STATUS HANDLING
                    // Backend may still say 'preparing' even when MeterValues are
                    // flowing (race condition). Treat as charging-in-progress so
                    // the UI moves to the monitoring screen where SSE will update
                    // live state as MeterValues arrive. If the local state ALREADY
                    // saw MeterValues, force the lifecycle straight to charging.
                    // ============================================================
                    const localLive = stateRef.current.live;
                    const localMeterActive = hasActiveMeterActivity(localLive);
                    const backendHasEnergy =
                        (session.energy_kwh != null && session.energy_kwh > 0) ||
                        session.tx_id != null;
                    const treatAsCharging = localMeterActive || backendHasEnergy;

                    console.log(
                        treatAsCharging
                            ? '[SessionContext] Session is preparing but evidence shows active charging — treating as charging'
                            : '[SessionContext] Session is preparing — routing to MONITOR_CHARGING for SSE'
                    );

                    targetStep = STEPS.MONITOR_CHARGING;
                    targetPaymentStep = PAYMENT_STEPS.DETAILS;

                    recoveredLiveState = {
                        ...localLive,
                        status: treatAsCharging ? 'Charging' : 'Preparing',
                        lifecycleState: treatAsCharging
                            ? LIFECYCLE_STATES.CHARGING
                            : (backendStatus === 'remote_start_requested' ? LIFECYCLE_STATES.STARTING : LIFECYCLE_STATES.PREPARING),
                        // Only real values. An invented transaction id / start
                        // time made the monitor treat a not-yet-started
                        // session as started: it skipped /start, and read a
                        // later Available as "charging ended" and sent the
                        // driver to Disconnect. StartTransaction brings both.
                        startTime: localLive?.startTime || session.started_at || null,
                        tx_id: realTxId(localLive?.tx_id) ?? realTxId(session.tx_id) ?? null,
                        // Use || (not ??) so a stale backend energy_kwh=0 cannot
                        // overwrite locally-observed energy preserved from cache.
                        energyKWh: session.energy_kwh || localLive?.energyKWh || 0,
                        // Preserve local MeterValues evidence on rehydration
                        hasReceivedMeterValues: localLive?.hasReceivedMeterValues || treatAsCharging,
                        lastMeterValueAt: localLive?.lastMeterValueAt || null,
                    };
                    break;
                }

                case 'starting':
                case 'active':
                case 'charging': {
                    // Stale check: charging sessions older than 12 hours are likely orphaned
                    const chargeStartedAt = new Date(session.started_at || session.created_at || session.createdAt);
                    const chargeAgeHours = (Date.now() - chargeStartedAt.getTime()) / 1000 / 60 / 60;

                    if (chargeAgeHours > 12) {
                        console.warn('[SessionContext]  Rehydration rejected — stale charging session (age:', chargeAgeHours.toFixed(1), 'hours)');
                        reset();
                        return { success: false, reason: 'stale_charging_session', ageHours: chargeAgeHours };
                    }

                    // ============================================================
                    // CRITICAL RECOVERY PATH: Backend says session is charging
                    // MUST recover UI into active charging mode with stop controls
                    // ============================================================
                    console.log('[SessionContext] BACKEND AUTHORITATIVE: Session is charging - forcing recovery to MONITOR_CHARGING');
                    
                    targetStep = STEPS.MONITOR_CHARGING;
                    targetPaymentStep = PAYMENT_STEPS.DETAILS;
                    
                    // Recover live state from backend session data if available
                    // This ensures UI has enough state to show stop button
                    recoveredLiveState = {
                        ...stateRef.current.live,
                        status: backendStatus === 'charging' || backendStatus === 'active' ? 'Charging' : 'Preparing',
                        lifecycleState: backendStatus === 'starting' ? LIFECYCLE_STATES.STARTING : LIFECYCLE_STATES.CHARGING,
                        // CRITICAL: If backend says charging, treat as started even without local tx_id
                        // This enables stop button and MeterValues acceptance
                        startTime: stateRef.current.live?.startTime || session.started_at || new Date().toISOString(),
                        // Keep existing tx_id if we have it, otherwise mark as recovering
                        // The server's transaction id when it has one. The
                        // placeholder remains only for a charging row with no
                        // tx_id yet (MeterValues before StartTransaction):
                        // the server says charging, and stop controls need it.
                        tx_id: realTxId(session.tx_id) ?? realTxId(stateRef.current.live?.tx_id) ?? `recovering-${session.sessionUid}`,
                        // Risk 2 fix: seed energyKWh from backend so the monitoring screen shows
                        // the correct value immediately, before SSE events start flowing again.
                        // Use || (not ??) so a stale backend energy_kwh=0 cannot
                        // overwrite locally-observed energy on recovery.
                        energyKWh: session.energy_kwh || stateRef.current.live?.energyKWh || 0,
                        // Cold-load correctness: seed idle + battery-full
                        // signals so the "Charging paused" / "Charging
                        // complete" banners render immediately on refresh,
                        // matching what a live client already sees. These
                        // fields also arrive later via SSE — this just
                        // eliminates the gap before the first event.
                        idleSince: session.idle?.idleSince ?? stateRef.current.live?.idleSince ?? null,
                        batteryFullSince: session.idle?.batteryFullSince ?? stateRef.current.live?.batteryFullSince ?? null,
                        chargingActivity: session.chargingActivity ?? stateRef.current.live?.chargingActivity ?? null,
                        isIdle: !!(session.idle?.idleSince),
                        hasReceivedMeterValues:
                            stateRef.current.live?.hasReceivedMeterValues ||
                            (session.energy_kwh != null && session.energy_kwh > 0),
                    };

                    console.log('[SessionContext] Recovered live state:', {
                        status: recoveredLiveState.status,
                        startTime: recoveredLiveState.startTime,
                        tx_id: recoveredLiveState.tx_id,
                        energyKWh: recoveredLiveState.energyKWh,
                        backendStatus: session.status
                    });
                    break;
                }
                
                case 'completed':
                case 'finalized':
                case 'stopped': {
                    // ════════════════════════════════════════════════════════
                    // STALE-RECEIPT GUARD (Phase D)
                    //
                    // A user clicking a 3-day-old receipt link should not be
                    // trapped in SUMMARY mode forever. After 24 hours we treat
                    // the receipt as stale: clear the local session and let the
                    // user land on the fresh-start screen rather than rehydrate
                    // into a session they almost certainly didn't mean to view.
                    //
                    // Within the 24-hour window we preserve the receipt view so
                    // a refresh during/just after a session still lands on the
                    // SUMMARY with the right energy / cost.
                    // ════════════════════════════════════════════════════════
                    const completedAt = new Date(
                        session.finalized_at || session.stopped_at || session.created_at || session.createdAt
                    );
                    const completedAgeHours =
                        (Date.now() - completedAt.getTime()) / 1000 / 60 / 60;
                    if (Number.isFinite(completedAgeHours) && completedAgeHours > 24) {
                        console.warn(
                            '[SessionContext]  Rehydration rejected — stale completed receipt (age:',
                            completedAgeHours.toFixed(1),
                            'hours)'
                        );
                        reset();
                        return {
                            success: false,
                            reason: 'stale_completed_receipt',
                            ageHours: completedAgeHours,
                        };
                    }
                    // Session completed — restore to SUMMARY so the user can see their receipt.
                    // Covers the "session finished while browser was closed" case.
                    console.log('[SessionContext] Session completed — restoring to SUMMARY step');

                    targetStep = STEPS.SUMMARY;
                    targetPaymentStep = PAYMENT_STEPS.DETAILS;

                    // Reconstruct live state from backend fields so the summary screen has data
                    // before it fetches its own serverSession copy.
                    // ============================================================
                    // CRITICAL FIX: Use || (not ??) for energyKWh to prevent
                    // backend zero values from overwriting locally-accumulated
                    // energy. Before finalization completes, backend may have
                    // energy_kwh=0 or final_energy_kwh=0 as default/placeholder.
                    // With ??, 0 is treated as valid and overwrites the real local value.
                    // With ||, 0 is falsy and falls through to the local value.
                    // ============================================================
                    recoveredLiveState = {
                        ...stateRef.current.live,
                        status: 'Available',
                        lifecycleState: LIFECYCLE_STATES.ENDED,
                        energyKWh: session.final_energy_kwh || session.energy_kwh || stateRef.current.live?.energyKWh || 0,
                        endTime: session.stopped_at ?? stateRef.current.live?.endTime ?? new Date().toISOString(),
                        startTime: session.started_at ?? stateRef.current.live?.startTime ?? null,
                        // tx_id is gone — block further MeterValues
                        tx_id: null,
                    };

                    // Session is done — active tracking no longer needed.
                    clearActiveSession();
                    break;
                }
                
                case 'failed':
                case 'voided':
                case 'expired':
                case 'cancelled':
                    // Session failed, voided, or expired - clear local session
                    // BUT: if the local state shows live MeterValues, we just
                    // raced the backend's expiry decision. Don't trust it —
                    // surface the active session and let recovery finish.
                    if (hasActiveMeterActivity(stateRef.current.live)) {
                        console.warn(
                            '[SessionContext] Backend says terminal but local MeterValues are flowing — ignoring (treating as charging)',
                            { backendStatus, status: session.status }
                        );
                        targetStep = STEPS.MONITOR_CHARGING;
                        targetPaymentStep = PAYMENT_STEPS.DETAILS;
                        recoveredLiveState = {
                            ...stateRef.current.live,
                            status: 'Charging',
                            lifecycleState: LIFECYCLE_STATES.CHARGING,
                        };
                        break;
                    }
                    console.warn('[SessionContext]  Rehydration rejected — session in terminal state:', session.status);
                    reset();
                    return { success: false, reason: 'session_terminal_state', status: session.status };

                default:
                    console.warn('[SessionContext] Unknown session status:', session.status, '(normalised:', backendStatus, ')');
            }

            console.log('[SessionContext]  Rehydration target:', {
                step: targetStep,
                stepName: Object.keys(STEPS).find(k => STEPS[k] === targetStep),
                paymentStep: targetPaymentStep
            });

            // Update local state with server data
            // Include recovered live state for charging sessions
            setState(s => ({
                ...s,
                step: targetStep,
                paymentStep: targetPaymentStep,
                sessionUid: session.sessionUid,
                sessionId: session.sessionUid, // Keep legacy field in sync
                stationId: session.station_id,
                connectorId: session.connector_id ?? s.connectorId,
                // CRITICAL: Update live state for charging recovery
                live: recoveredLiveState,
                // Update payment info if available
                payment: (!session.payment_authorization
                    && s.payment?.status === 'authorizing'
                    && !isPreauthActive(session.sessionUid))
                    ? { ...s.payment, status: null, needsReconcile: true }
                    : session.payment_authorization ? {
                    ...s.payment,
                    status:
                        backendStatus === 'authorized' || backendStatus === 'starting' || backendStatus === 'charging' || backendStatus === 'preparing' || backendStatus === 'active'
                            ? 'authorized'
                            : s.payment?.status,
                    authorizationId: session.payment_authorization.authorization_id,
                    transactionId: session.payment_authorization.transaction_id,
                    orderId: session.payment_authorization.order_id,
                } : s.payment,
                // Restore the receipt contact from the server. The API sends
                // it as `receipt` ({ email, full_name }); this used to read
                // `receipt_info`, which the API never sent, so any cold
                // resume -- Safari purging the tab, or code recovery in a
                // fresh browser -- lost the receipt email.
                receiptInfo: (() => {
                    const r = session.receipt || session.receipt_info;
                    return r?.email ? { fullName: r.full_name || null, email: r.email } : s.receiptInfo;
                })(),
                updatedAt: Date.now()
            }));

            return { success: true, session, targetStep };
        } catch (error) {
            console.error('[SessionContext]  Exception during rehydration:', error);
            // Clear session on exception to prevent crashes
            console.warn('[SessionContext] Clearing session due to exception');
            reset();
            return { success: false, reason: 'exception', error: error.message };
        }
    }, [reset]);


    /**
     * Mark bootstrap as complete.
     * Called by App.jsx AFTER recovery logic has finished.
     * This allows the URL sync effect to start cleaning up stale URL params.
     */
    const markBootstrapComplete = useCallback(() => {
        console.log('[SessionContext] ✅ Bootstrap marked complete — URL sync enabled');
        bootstrapCompleteRef.current = true;
    }, []);

    // Memoize the context value
    // State IS in dependencies so components get fresh state
    // All functions are stable (useCallback with empty deps) so they won't cause infinite loops
    const value = useMemo(() => ({
        state,
        setStep,
        setPaymentStep,
        setAmount,
        update,
        setAccessOutcome,
        reset,
        setDebugMode,
        // station helpers
        setStationId,
        setConnectorId,
        setPublicId,
        setStationMeta,
        setStationData,
        clearStation,
        // session control
        cancelSession,
        stopSession,
        // rehydration
        rehydrateSession,
        // bootstrap lifecycle
        markBootstrapComplete,
        // auth-required containment
        markAuthFailed,
        markAuthRoundTrip,
        clearAuthRoundTrip,
        clearAuthFailed,
        // fresh-flow payment-safety reset
        prepareForNewSession,
        // session-existence boundary — see the block-comment on
        // ensureCustomerSession above for the exact contract.
        ensureCustomerSession,
    }), [state, setStep, setPaymentStep, setAmount, update, setAccessOutcome, reset, setDebugMode, setStationId, setConnectorId, setPublicId, setStationMeta, setStationData, clearStation, cancelSession, stopSession, rehydrateSession, markBootstrapComplete, markAuthFailed, clearAuthFailed, markAuthRoundTrip, clearAuthRoundTrip, prepareForNewSession, ensureCustomerSession]);

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
    const ctx = useContext(SessionContext);
    if (!ctx) throw new Error("useSession must be used within <SessionProvider>");

    return ctx;
}

/**
 * Safer variant for components that may render outside the SessionProvider
 * — used by the NativeBridge in tests where the route tree renders in a
 * bare MemoryRouter without the full provider stack. Returns `null` on
 * miss so a caller can guard with `if (!session) return null;` rather
 * than having to mock the provider from every test file.
 *
 * Production callers inside SessionProvider still see the full context.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalSession() {
    return useContext(SessionContext);
}
